// app/company/[slug]/CompanyProfileClient.tsx
"use client";

import React from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Responsive, type ResponsiveLayouts, useContainerWidth } from "react-grid-layout";
import {
  containerBounds,
  gridBounds,
  snapToGrid,
  minSize,
  maxSize,
  noCompactor,
  type LayoutItem,
} from "react-grid-layout/core";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Download, ExternalLink, SlidersHorizontal, Maximize, Minimize2, RefreshCw, ArrowLeft, MapPin } from "lucide-react";
import { revalidateCompanyPage } from "./actions";
import { useMobile } from "@/lib/use-mobile";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HowPanel } from "@/components/company/panels/HowPanel";
import { PeersPanel } from "@/components/company/panels/PeersPanel";
import { enableCompanyProfilePdfExport, enableCompanyProfileLayoutEdit } from "@/app/lib/experiments";
import { RisksPanel } from "@/components/company/panels/RisksPanel";
import { WhatPanel } from "@/components/company/panels/WhatPanel";
import { WherePanel } from "@/components/company/panels/WherePanel";
import { WhoPanel } from "@/components/company/panels/WhoPanel";
import type { PanelMeta } from "@/components/company/panels/PanelShell";
import { DatadiniIcon, DatadiniLogo } from "@/components/Icons";
import { DatadiniHighlightLogo } from "@/components/DatadiniHighlightLogo";
import { buildClipboardText, buildCsv, exportToExcel, type ExportSection } from "@/app/lib/export-utils";
import { useSearchMode, useSearchActive } from "@/lib/search-mode-atom";
import { useTheme } from "next-themes";
import lseTickerMap from "@/data/lse_ticker_map.json";

interface CompanyDocument {
  company_number: string;
  company_name: string;
  company_status: string;
  company_type?: string;
  incorporation_date?: string;
  dissolution_date?: string;
  registered_address_line_1?: string;
  registered_address_line_2?: string;
  registered_address_town?: string;
  registered_address_county?: string;
  registered_address_postcode?: string;
  full_address?: string;
  sector?: string;
  sic_code_1?: string;
  sic_text_1?: string;
  sic_code_2?: string;
  sic_text_2?: string;
  latest_turnover?: number;
  latest_total_assets?: number;
  latest_profit_before_tax?: number;
  latest_employees?: number;
  latest_accounts_date?: string;
  latitude?: number;
  longitude?: number;
  last_updated?: string;
}

interface CompanySnapshot {
  Company_Number?: string | null;
  Company_Name?: string | null;
  Company_Status?: string | null;
  Company_Type?: string | null;
  Incorporation_Date?: string | null;
  Company_Age_Years?: number | null;
  Age_Band?: string | null;
  Companies_House_Link?: string | null;
  SIC_Code?: string | null;
  Section_Code?: string | null;
  Sector?: string | null;
  Industry?: string | null;
  Division?: string | null;
  SIC_Code_1?: string | null;
  SIC_Text_1?: string | null;
  SIC_Code_2?: string | null;
  SIC_Text_2?: string | null;
  Turnover_Year1?: number | null;
  Turnover_Year2?: number | null;
  Turnover_Year3?: number | null;
  Turnover_Year4?: number | null;
  Turnover_Band?: string | null;
  Cost_Of_Sales_Year1?: number | null;
  Cost_Of_Sales_Year2?: number | null;
  Cost_Of_Sales_Year3?: number | null;
  Gross_Profit_Year1?: number | null;
  Gross_Profit_Year2?: number | null;
  Gross_Profit_Year3?: number | null;
  Operating_Profit_Year1?: number | null;
  Operating_Profit_Year2?: number | null;
  Operating_Profit_Year3?: number | null;
  Profit_Before_Tax_Year1?: number | null;
  Profit_Before_Tax_Year2?: number | null;
  Profit_Before_Tax_Year3?: number | null;
  Profit_Before_Tax_Year4?: number | null;
  Profit_After_Tax_Year1?: number | null;
  Profit_After_Tax_Year2?: number | null;
  Profit_After_Tax_Year3?: number | null;
  Cash_Year1?: number | null;
  Cash_Year2?: number | null;
  Cash_Year3?: number | null;
  Total_Debt_Year1?: number | null;
  Total_Debt_Year2?: number | null;
  Total_Debt_Year3?: number | null;
  Net_Debt_Year1?: number | null;
  Net_Debt_Year2?: number | null;
  Net_Debt_Year3?: number | null;
  Short_Term_Debt_Year1?: number | null;
  Short_Term_Debt_Year2?: number | null;
  Short_Term_Debt_Year3?: number | null;
  Long_Term_Debt_Year1?: number | null;
  Long_Term_Debt_Year2?: number | null;
  Long_Term_Debt_Year3?: number | null;
  Total_Assets_Year1?: number | null;
  Total_Assets_Year2?: number | null;
  Total_Assets_Year3?: number | null;
  Current_Assets_Year1?: number | null;
  Current_Assets_Year2?: number | null;
  Current_Assets_Year3?: number | null;
  Fixed_Assets_Year1?: number | null;
  Fixed_Assets_Year2?: number | null;
  Fixed_Assets_Year3?: number | null;
  Current_Liabilities_Year1?: number | null;
  Current_Liabilities_Year2?: number | null;
  Current_Liabilities_Year3?: number | null;
  Creditors_After_One_Year_Year1?: number | null;
  Creditors_After_One_Year_Year2?: number | null;
  Creditors_After_One_Year_Year3?: number | null;
  Total_Liabilities_Year1?: number | null;
  Total_Liabilities_Year2?: number | null;
  Total_Liabilities_Year3?: number | null;
  Total_Equity_Year1?: number | null;
  Total_Equity_Year2?: number | null;
  Total_Equity_Year3?: number | null;
  Employees_Year1?: number | null;
  Employees_Year2?: number | null;
  Employees_Year3?: number | null;
  Employee_Band?: string | null;
  Year1_FY?: number | null;
  Year2_FY?: number | null;
  Year3_FY?: number | null;
  Year1_Period_End?: string | null;
  Year2_Period_End?: string | null;
  Year3_Period_End?: string | null;
  Currency?: string | null;
  Corporation_Tax_Payable_Year1?: number | null;
  Corporation_Tax_Payable_Year2?: number | null;
  Corporation_Tax_Payable_Year3?: number | null;
  Current_Tax_For_Period_Year1?: number | null;
  Current_Tax_For_Period_Year2?: number | null;
  Current_Tax_For_Period_Year3?: number | null;
  Effective_Tax_Rate_Year1?: number | null;
  Effective_Tax_Rate_Year2?: number | null;
  Effective_Tax_Rate_Year3?: number | null;
  ETR_Anomaly_Flag?: string | null;
  Filing_Software?: string | null;
  has_interim_periods?: boolean | number | null;
  has_acquisitions?: boolean | number | null;
  has_geo_segments?: boolean | number | null;
  has_restated?: boolean | number | null;
  has_extraordinary?: boolean | number | null;
  has_operating_segments?: boolean | number | null;
  high_complexity?: boolean | number | null;
  very_high_fact_count?: boolean | number | null;
  Accounts_Date?: string | null;
  Post_Town?: string | null;
  County?: string | null;
  Country_Region?: string | null;
  Postcode?: string | null;
  Address_Line_1?: string | null;
  Address_Line_2?: string | null;
  Latitude?: number | null;
  Longitude?: number | null;
  Director_Count?: number | null;
  Board_Structure?: string | null;
  Company_Secretary_Count?: number | null;
  First_Director?: string | null;
  Director_Min_Age?: number | null;
  Director_Max_Age?: number | null;
  Director_Avg_Age?: number | null;
  confidence_level?: string | null;
  is_reliable?: boolean | number | null;
  red_flag_count?: number | null;
}

type Officer = {
  Hash_Key: string;
  Full_Name: string;
  Appointment_Type: string;
  Appointment_Date: string;
  Resignation_Date?: string | null;
  Title?: string | null;
  Forenames?: string | null;
  Surname?: string | null;
  Partial_Date_Of_Birth?: string | null;
  Occupation?: string | null;
  Nationality?: string | null;
  Full_Address?: string | null;
  Address_Line_1?: string | null;
  Address_Line_2?: string | null;
  Post_Town?: string | null;
  County?: string | null;
  Country?: string | null;
  Person_Postcode?: string | null;
  Corporate_Indicator?: string | null;
  Person_Number?: string | null;
};

type OfficerResponse = {
  active_officers: Officer[];
  resigned_officers: Officer[];
  summary: {
    total_officers: number;
    active_directors: number;
    resigned_directors: number;
    company_secretaries: number;
    corporate_directors: number;
  };
};

type Psc = {
  Hash_Key: string;
  Data_Name: string;
  Data_Kind: string;
  Natures_Of_Control: string[];
  Data_Name_Elements_Title?: string | null;
  Data_Name_Elements_Forename?: string | null;
  Data_Name_Elements_Surname?: string | null;
  Data_Date_Of_Birth_Month?: number | null;
  Data_Date_Of_Birth_Year?: number | null;
  Data_Nationality?: string | null;
  Data_Country_Of_Residence?: string | null;
  Data_Identification_Legal_Form?: string | null;
  Data_Identification_Legal_Authority?: string | null;
  Data_Identification_Registration_Number?: string | null;
  Data_Identification_Country_Registered?: string | null;
  Data_Identification_Place_Registered?: string | null;
  Full_Address?: string | null;
  Data_Address_Premises?: string | null;
  Data_Address_Address_Line_1?: string | null;
  Data_Address_Address_Line_2?: string | null;
  Data_Address_Locality?: string | null;
  Data_Address_Postal_Code?: string | null;
  Data_Address_Country?: string | null;
  Data_Address_Region?: string | null;
  Data_Identity_Verification_Details_Identity_Verified_On?: string | null;
  Data_Is_Sanctioned?: boolean | number | null;
  Data_Ceased?: boolean | number | null;
  Data_Ceased_On?: string | null;
  Data_Notified_On?: string | null;
};

type PscResponse = {
  active_pscs: Psc[];
  ceased_pscs: Psc[];
  summary: {
    total_pscs: number;
    active_pscs: number;
    ceased_pscs: number;
    individual_pscs: number;
    corporate_pscs: number;
    sanctioned_pscs: number;
  };
};

type GazetteNotice = {
  Hash_Key: string;
  Notice_Type: string;
  Notice_Code: string;
  Gazette_Date: string;
  Last_Modified_Date?: string | null;
  Company_Name?: string | null;
  Company_Number?: string | null;
  Record_Type?: number | null;
  Sequence_Number?: number | null;
};

type GazetteResponse = {
  notices: GazetteNotice[];
  summary: {
    total_notices: number;
    latest_notice_date: string | null;
    notice_types: Record<string, number>;
  };
};

type PeerCompany = {
  Company_Number: string;
  Company_Name: string;
  Company_Status?: string | null;
  Incorporation_Date?: string | null;
  Age_Band?: string | null;
  Turnover_Year1?: number | null;
  Turnover_Band?: string | null;
  Profit_After_Tax_Year1?: number | null;
  Total_Assets_Year1?: number | null;
  Total_Debt_Year1?: number | null;
  Cash_Year1?: number | null;
  Employee_Band?: string | null;
  Industry?: string | null;
  Division?: string | null;
  County?: string | null;
  Post_Town?: string | null;
  Postcode?: string | null;
  Companies_House_Link?: string | null;
};

type PeersResponse = {
  count: number;
  results: PeerCompany[];
};

type CompanyContact = {
  Company_Number: string;
  Company_Name: string;
  Website?: string | null;
  Domain?: string | null;
  Email?: string | null;
  Phone?: string | null;
  Twitter?: string | null;
  LinkedIn?: string | null;
  Facebook?: string | null;
  Instagram?: string | null;
  YouTube?: string | null;
  AI_Confidence?: string | null;
  Status?: string | null;
  Found_At?: string | null;
  Updated_At?: string | null;
};

interface CompanyProfileClientProps {
  company: CompanyDocument;
  initialMode?: boolean;
  showDebug?: boolean;
  reportMode?: boolean;
  initialHeaderColor?: string | null;
}

function parseNumberValue(value: unknown) {
  if (value === null || value === undefined || value === "") return null;
  const raw = typeof value === "string" ? value.trim().replace(/,/g, "") : value;
  const num = Number(raw);
  if (Number.isFinite(num)) return num;
  if (typeof raw === "string") {
    const match = raw.match(/[-+]?\d+(\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      return Number.isFinite(parsed) ? parsed : null;
    }
  }
  return null;
}

function normalizeSnapshot(raw: Record<string, unknown> | null): CompanySnapshot | null {
  if (!raw) return null;
  const lower: Record<string, unknown> = {};
  const normalizeKey = (key: string) => key.trim().toLowerCase();
  for (const [key, value] of Object.entries(raw)) {
    lower[normalizeKey(key)] = value;
  }

  const getValue = (...keys: string[]) => {
    for (const key of keys) {
      if (key in raw) return raw[key];
      const lowerKey = normalizeKey(key);
      if (lowerKey in lower) return lower[lowerKey];
    }
    return null;
  };

  const toNumber = (value: unknown) => parseNumberValue(value);

  return {
    Company_Number: getValue("Company_Number", "company_number") as string | null,
    Company_Name: getValue("Company_Name", "company_name") as string | null,
    Company_Status: getValue("Company_Status", "company_status") as string | null,
    Company_Type: getValue("Company_Type", "company_type") as string | null,
    Incorporation_Date: getValue("Incorporation_Date", "incorporation_date") as string | null,
    Company_Age_Years: toNumber(getValue("Company_Age_Years", "company_age_years")),
    Age_Band: getValue("Age_Band", "age_band") as string | null,
    Companies_House_Link: getValue("Companies_House_Link", "companies_house_link") as string | null,
    SIC_Code: getValue("SIC_Code", "sic_code") as string | null,
    Section_Code: getValue("Section_Code", "section_code") as string | null,
    Sector: getValue("Sector", "sector") as string | null,
    Industry: getValue("Industry", "industry") as string | null,
    Division: getValue("Division", "division") as string | null,
    SIC_Code_1: getValue("SIC_Code_1", "sic_code_1") as string | null,
    SIC_Text_1: getValue("SIC_Text_1", "sic_text_1") as string | null,
    SIC_Code_2: getValue("SIC_Code_2", "sic_code_2") as string | null,
    SIC_Text_2: getValue("SIC_Text_2", "sic_text_2") as string | null,
    Turnover_Year1: toNumber(getValue("Turnover_Year1", "turnover_year1")),
    Turnover_Year2: toNumber(getValue("Turnover_Year2", "turnover_year2")),
    Turnover_Year3: toNumber(getValue("Turnover_Year3", "turnover_year3")),
    Turnover_Year4: toNumber(getValue("Turnover_Year4", "turnover_year4")),
    Turnover_Band: getValue("Turnover_Band", "turnover_band") as string | null,
    Cost_Of_Sales_Year1: toNumber(getValue("Cost_Of_Sales_Year1", "cost_of_sales_year1")),
    Cost_Of_Sales_Year2: toNumber(getValue("Cost_Of_Sales_Year2", "cost_of_sales_year2")),
    Cost_Of_Sales_Year3: toNumber(getValue("Cost_Of_Sales_Year3", "cost_of_sales_year3")),
    Gross_Profit_Year1: toNumber(getValue("Gross_Profit_Year1", "gross_profit_year1")),
    Gross_Profit_Year2: toNumber(getValue("Gross_Profit_Year2", "gross_profit_year2")),
    Gross_Profit_Year3: toNumber(getValue("Gross_Profit_Year3", "gross_profit_year3")),
    Operating_Profit_Year1: toNumber(getValue("Operating_Profit_Year1", "operating_profit_year1")),
    Operating_Profit_Year2: toNumber(getValue("Operating_Profit_Year2", "operating_profit_year2")),
    Operating_Profit_Year3: toNumber(getValue("Operating_Profit_Year3", "operating_profit_year3")),
    Profit_Before_Tax_Year1: toNumber(getValue("Profit_Before_Tax_Year1", "profit_before_tax_year1")),
    Profit_Before_Tax_Year2: toNumber(getValue("Profit_Before_Tax_Year2", "profit_before_tax_year2")),
    Profit_Before_Tax_Year3: toNumber(getValue("Profit_Before_Tax_Year3", "profit_before_tax_year3")),
    Profit_Before_Tax_Year4: toNumber(getValue("Profit_Before_Tax_Year4", "profit_before_tax_year4")),
    Profit_After_Tax_Year1: toNumber(getValue("Profit_After_Tax_Year1", "profit_after_tax_year1")),
    Profit_After_Tax_Year2: toNumber(getValue("Profit_After_Tax_Year2", "profit_after_tax_year2")),
    Profit_After_Tax_Year3: toNumber(getValue("Profit_After_Tax_Year3", "profit_after_tax_year3")),
    Cash_Year1: toNumber(getValue("Cash_Year1", "cash_year1")),
    Cash_Year2: toNumber(getValue("Cash_Year2", "cash_year2")),
    Cash_Year3: toNumber(getValue("Cash_Year3", "cash_year3")),
    Total_Debt_Year1: toNumber(getValue("Total_Debt_Year1", "total_debt_year1")),
    Total_Debt_Year2: toNumber(getValue("Total_Debt_Year2", "total_debt_year2")),
    Total_Debt_Year3: toNumber(getValue("Total_Debt_Year3", "total_debt_year3")),
    Net_Debt_Year1: toNumber(getValue("Net_Debt_Year1", "net_debt_year1")),
    Net_Debt_Year2: toNumber(getValue("Net_Debt_Year2", "net_debt_year2")),
    Net_Debt_Year3: toNumber(getValue("Net_Debt_Year3", "net_debt_year3")),
    Short_Term_Debt_Year1: toNumber(getValue("Short_Term_Debt_Year1", "short_term_debt_year1")),
    Short_Term_Debt_Year2: toNumber(getValue("Short_Term_Debt_Year2", "short_term_debt_year2")),
    Short_Term_Debt_Year3: toNumber(getValue("Short_Term_Debt_Year3", "short_term_debt_year3")),
    Long_Term_Debt_Year1: toNumber(getValue("Long_Term_Debt_Year1", "long_term_debt_year1")),
    Long_Term_Debt_Year2: toNumber(getValue("Long_Term_Debt_Year2", "long_term_debt_year2")),
    Long_Term_Debt_Year3: toNumber(getValue("Long_Term_Debt_Year3", "long_term_debt_year3")),
    Total_Assets_Year1: toNumber(getValue("Total_Assets_Year1", "total_assets_year1")),
    Total_Assets_Year2: toNumber(getValue("Total_Assets_Year2", "total_assets_year2")),
    Total_Assets_Year3: toNumber(getValue("Total_Assets_Year3", "total_assets_year3")),
    Current_Assets_Year1: toNumber(getValue("Current_Assets_Year1", "current_assets_year1")),
    Current_Assets_Year2: toNumber(getValue("Current_Assets_Year2", "current_assets_year2")),
    Current_Assets_Year3: toNumber(getValue("Current_Assets_Year3", "current_assets_year3")),
    Fixed_Assets_Year1: toNumber(getValue("Fixed_Assets_Year1", "fixed_assets_year1")),
    Fixed_Assets_Year2: toNumber(getValue("Fixed_Assets_Year2", "fixed_assets_year2")),
    Fixed_Assets_Year3: toNumber(getValue("Fixed_Assets_Year3", "fixed_assets_year3")),
    Current_Liabilities_Year1: toNumber(getValue("Current_Liabilities_Year1", "current_liabilities_year1")),
    Current_Liabilities_Year2: toNumber(getValue("Current_Liabilities_Year2", "current_liabilities_year2")),
    Current_Liabilities_Year3: toNumber(getValue("Current_Liabilities_Year3", "current_liabilities_year3")),
    Creditors_After_One_Year_Year1: toNumber(getValue("Creditors_After_One_Year_Year1", "creditors_after_one_year_year1")),
    Creditors_After_One_Year_Year2: toNumber(getValue("Creditors_After_One_Year_Year2", "creditors_after_one_year_year2")),
    Creditors_After_One_Year_Year3: toNumber(getValue("Creditors_After_One_Year_Year3", "creditors_after_one_year_year3")),
    Total_Liabilities_Year1: toNumber(getValue("Total_Liabilities_Year1", "total_liabilities_year1")),
    Total_Liabilities_Year2: toNumber(getValue("Total_Liabilities_Year2", "total_liabilities_year2")),
    Total_Liabilities_Year3: toNumber(getValue("Total_Liabilities_Year3", "total_liabilities_year3")),
    Total_Equity_Year1: toNumber(getValue("Total_Equity_Year1", "total_equity_year1")),
    Total_Equity_Year2: toNumber(getValue("Total_Equity_Year2", "total_equity_year2")),
    Total_Equity_Year3: toNumber(getValue("Total_Equity_Year3", "total_equity_year3")),
    Employees_Year1: toNumber(getValue("Employees_Year1", "employees_year1")),
    Employees_Year2: toNumber(getValue("Employees_Year2", "employees_year2")),
    Employees_Year3: toNumber(getValue("Employees_Year3", "employees_year3")),
    Employee_Band: getValue("Employee_Band", "employee_band") as string | null,
    Year1_FY: toNumber(getValue("Year1_FY", "year1_fy")),
    Year2_FY: toNumber(getValue("Year2_FY", "year2_fy")),
    Year3_FY: toNumber(getValue("Year3_FY", "year3_fy")),
    Year1_Period_End: getValue("Year1_Period_End", "year1_period_end") as string | null,
    Year2_Period_End: getValue("Year2_Period_End", "year2_period_end") as string | null,
    Year3_Period_End: getValue("Year3_Period_End", "year3_period_end") as string | null,
    Currency: getValue("Currency", "currency") as string | null,
    Corporation_Tax_Payable_Year1: toNumber(getValue("Corporation_Tax_Payable_Year1", "corporation_tax_payable_year1")),
    Corporation_Tax_Payable_Year2: toNumber(getValue("Corporation_Tax_Payable_Year2", "corporation_tax_payable_year2")),
    Corporation_Tax_Payable_Year3: toNumber(getValue("Corporation_Tax_Payable_Year3", "corporation_tax_payable_year3")),
    Current_Tax_For_Period_Year1: toNumber(getValue("Current_Tax_For_Period_Year1", "current_tax_for_period_year1")),
    Current_Tax_For_Period_Year2: toNumber(getValue("Current_Tax_For_Period_Year2", "current_tax_for_period_year2")),
    Current_Tax_For_Period_Year3: toNumber(getValue("Current_Tax_For_Period_Year3", "current_tax_for_period_year3")),
    Effective_Tax_Rate_Year1: toNumber(getValue("Effective_Tax_Rate_Year1", "effective_tax_rate_year1")),
    Effective_Tax_Rate_Year2: toNumber(getValue("Effective_Tax_Rate_Year2", "effective_tax_rate_year2")),
    Effective_Tax_Rate_Year3: toNumber(getValue("Effective_Tax_Rate_Year3", "effective_tax_rate_year3")),
    ETR_Anomaly_Flag: getValue("ETR_Anomaly_Flag", "etr_anomaly_flag") as string | null,
    Filing_Software: getValue("Filing_Software", "filing_software") as string | null,
    Accounts_Date: getValue("Accounts_Date", "accounts_date") as string | null,
    Post_Town: getValue("Post_Town", "post_town") as string | null,
    County: getValue("County", "county") as string | null,
    Country_Region: getValue("Country_Region", "country_region") as string | null,
    Postcode: getValue("Postcode", "postcode") as string | null,
    Address_Line_1: getValue("Address_Line_1", "address_line_1", "Address_Line1", "AddressLine1") as string | null,
    Address_Line_2: getValue("Address_Line_2", "address_line_2", "Address_Line2", "AddressLine2") as string | null,
    Latitude: toNumber(getValue("Latitude", "latitude", "lat")),
    Longitude: toNumber(getValue("Longitude", "longitude", "lng", "lon", "long")),
    Director_Count: toNumber(getValue("Director_Count", "director_count")),
    Board_Structure: getValue("Board_Structure", "board_structure") as string | null,
    Company_Secretary_Count: toNumber(getValue("Company_Secretary_Count", "company_secretary_count")),
    First_Director: getValue("First_Director", "first_director") as string | null,
    Director_Min_Age: toNumber(getValue("Director_Min_Age", "director_min_age")),
    Director_Max_Age: toNumber(getValue("Director_Max_Age", "director_max_age")),
    Director_Avg_Age: toNumber(getValue("Director_Avg_Age", "director_avg_age")),
    confidence_level: getValue("confidence_level", "Confidence_Level") as string | null,
    is_reliable: getValue("is_reliable", "Is_Reliable") as boolean | number | null,
    red_flag_count: toNumber(getValue("red_flag_count", "Red_Flag_Count")),
    has_interim_periods: getValue("has_interim_periods", "Has_Interim_Periods") as boolean | number | null,
    has_acquisitions: getValue("has_acquisitions", "Has_Acquisitions") as boolean | number | null,
    has_geo_segments: getValue("has_geo_segments", "Has_Geo_Segments") as boolean | number | null,
    has_restated: getValue("has_restated", "Has_Restated") as boolean | number | null,
    has_extraordinary: getValue("has_extraordinary", "Has_Extraordinary") as boolean | number | null,
    has_operating_segments: getValue("has_operating_segments", "Has_Operating_Segments") as boolean | number | null,
    high_complexity: getValue("high_complexity", "High_Complexity") as boolean | number | null,
    very_high_fact_count: getValue("very_high_fact_count", "Very_High_Fact_Count") as boolean | number | null,
  };
}

function getRawSnapshotValue(raw: Record<string, unknown> | null, ...keys: string[]) {
  if (!raw) return null;
  const lower: Record<string, unknown> = {};
  const normalizeKey = (key: string) => key.trim().toLowerCase();
  for (const [key, value] of Object.entries(raw)) {
    lower[normalizeKey(key)] = value;
  }
  for (const key of keys) {
    if (key in raw) return raw[key];
    const lowerKey = normalizeKey(key);
    if (lowerKey in lower) return lower[lowerKey];
  }
  return null;
}

function getLooseLatLon(raw: Record<string, unknown> | null) {
  if (!raw) return null;
  let lat: number | null = null;
  let lon: number | null = null;
  for (const [key, value] of Object.entries(raw)) {
    const normalized = key.trim().toLowerCase();
    if (lat === null && (normalized.includes("latitude") || normalized === "lat")) {
      const num = parseNumberValue(value);
      if (Number.isFinite(num)) lat = num;
    }
    if (lon === null && (normalized.includes("longitude") || normalized === "lon" || normalized === "lng" || normalized === "long")) {
      const num = parseNumberValue(value);
      if (Number.isFinite(num)) lon = num;
    }
    if (lat !== null && lon !== null) break;
  }
  if (lat === null || lon === null) return null;
  return { lat, lon };
}

// Helpers
function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `£${(value / 1_000).toFixed(0)}K`;
  return `£${value.toLocaleString()}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr || dateStr.startsWith("0000-00-00")) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function formatAgeDetailed(dateStr?: string | null) {
  if (!dateStr) return "—";
  const start = new Date(dateStr);
  if (Number.isNaN(start.getTime())) return "—";
  const now = new Date();
  if (now < start) return "—";

  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();

  if (days < 0) {
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    days += prevMonth;
    months -= 1;
  }
  if (months < 0) {
    months += 12;
    years -= 1;
  }

  return `${years}y ${months}m ${days}d`;
}
function formatMonthYear(month?: number | null, year?: number | null) {
  if (!month || !year) return "—";
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

type PreviousNameEntry = { name: string; from?: string | null; to?: string | null };



function parsePreviousNames(rawValue: unknown): PreviousNameEntry[] | null {
  if (!rawValue) return null;
  const normalizeEntry = (entry: any) => {
    if (!entry) return null;
    if (typeof entry === "string") return { name: entry };
    return {
      name: entry.Name ?? entry.name ?? entry.Company_Name ?? entry.company_name ?? null,
      from: entry.From ?? entry.from ?? entry.Start ?? entry.start ?? entry.Period_From ?? entry.period_from ?? null,
      to: entry.To ?? entry.to ?? entry.End ?? entry.end ?? entry.Period_To ?? entry.period_to ?? null,
    };
  };
  const isValid = (entry: any): entry is PreviousNameEntry => !!entry?.name;
  if (Array.isArray(rawValue)) {
    const parsed = rawValue.map(normalizeEntry).filter(isValid);
    return parsed.length > 0 ? parsed : null;
  }
  if (typeof rawValue === "string") {
    const trimmed = rawValue.trim();
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsePreviousNames(parsed);
      } catch {
        return [{ name: rawValue }];
      }
    }
    return [{ name: rawValue }];
  }
  const normalized = normalizeEntry(rawValue);
  return isValid(normalized) ? [normalized] : null;
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function normalizeText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const raw = typeof value === "string" ? value.trim() : String(value);
  return raw.length > 0 ? raw : null;
}

function splitSic(value?: string | null) {
  if (!value) return { code: null, text: null };
  const parts = value.split(" - ");
  if (parts.length >= 2) {
    const code = normalizeText(parts[0]);
    const text = normalizeText(parts.slice(1).join(" - "));
    return { code, text };
  }
  return { code: normalizeText(value), text: null };
}

function formatMoney(value?: number | null) {
  if (!isNumber(value)) return "—";
  const absValue = Math.abs(value);
  const maximumFractionDigits = absValue >= 1_000_000 ? 1 : absValue >= 1_000 ? 1 : 0;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits,
  }).format(value);
}

function formatPercent(value?: number | null) {
  if (!isNumber(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

type LseTickerEntry = { ticker: string; name: string; source: string };
const LSE_TICKERS = lseTickerMap as Record<string, LseTickerEntry>;

const normalizeLseName = (value: string) =>
  value
    .toUpperCase()
    .replace(/\b(THE|PLC|PUBLIC LIMITED COMPANY|LTD|LIMITED)\b/g, "")
    .replace(/&/g, "AND")
    .replace(/[^A-Z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getLseTicker = (value?: string | null) => {
  if (!value) return null;
  const normalized = normalizeLseName(value);
  return LSE_TICKERS[normalized]?.ticker ?? null;
};

type LseQuote = {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
};

const formatStockPrice = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatStockChange = (change: number, changePercent: number) => {
  const changeValue = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(change);
  const signedChange = `${change > 0 ? "+" : ""}${changeValue}`;
  const direction = change > 0 ? "Up" : change < 0 ? "Down" : "Flat";
  if (!Number.isFinite(changePercent)) return signedChange;
  const signedPercent = `${changePercent > 0 ? "+" : ""}${changePercent.toFixed(2)}%`;
  return `${direction} ${signedChange} (${signedPercent})`;
};



function formatBoolean(value?: boolean | number | null) {
  if (value === null || value === undefined) return "—";
  if (value === true || value === 1) return "Yes";
  if (value === false || value === 0) return "No";
  return String(value);
}

const CONTROL_TYPE_LABELS: Record<string, string> = {
  "ownership-of-shares-25-to-50-percent": "25-50% shares",
  "ownership-of-shares-50-to-75-percent": "50-75% shares",
  "ownership-of-shares-75-to-100-percent": "75-100% shares",
  "ownership-of-shares-more-than-25-percent-as-trust": "25%+ shares (trust)",
  "ownership-of-shares-more-than-25-percent-as-firm": "25%+ shares (firm)",
  "voting-rights-25-to-50-percent": "25-50% votes",
  "voting-rights-50-to-75-percent": "50-75% votes",
  "voting-rights-75-to-100-percent": "75-100% votes",
  "voting-rights-more-than-25-percent-as-trust": "25%+ votes (trust)",
  "voting-rights-more-than-25-percent-as-firm": "25%+ votes (firm)",
  "right-to-appoint-and-remove-directors": "Can appoint/remove directors",
  "right-to-appoint-and-remove-directors-as-trust": "Can appoint/remove (trust)",
  "right-to-appoint-and-remove-directors-as-firm": "Can appoint/remove (firm)",
  "significant-influence-or-control": "Significant influence/control",
  "significant-influence-or-control-as-trust": "Significant influence (trust)",
  "significant-influence-or-control-as-firm": "Significant influence (firm)",
};

const PROFILE_PANELS: PanelMeta[] = [
  {
    key: "what",
    title: "What is it?",
    subtitle: "Company overview and contact details",
    description:
      "Official details, current status, and a clear summary of what the company does.",
    className: "lg:col-span-2",
  },
  {
    key: "who",
    title: "Who runs it?",
    subtitle: "Directors and ownership",
    description:
      "The people behind the company, including directors and ownership where available.",
  },
  {
    key: "where",
    title: "Where is it?",
    subtitle: "Registered address and location",
    description: "Registered address and location details for filtering and verification.",
  },
  {
    key: "how",
    title: "How is it doing?",
    subtitle: "Financials and accounts",
    description: "Key figures from the latest accounts, with trends and data-quality notes.",
    className: "lg:col-span-2",
  },
  {
    key: "risks",
    title: "Any risks or official notices?",
    subtitle: "Risk, Gazette, and charges",
    description: "Official notices and legal signals, including Gazette items and charges.",
  },
  {
    key: "peers",
    title: "Who are its peers?",
    subtitle: "Peers and competition",
    description: "Similar companies based on industry and size, to benchmark performance and context quickly.",
  },
];

const isHighRiskNotice = (noticeType?: string | null) => {
  if (!noticeType) return false;
  const value = noticeType.toLowerCase();
  return (
    value.includes("strike-off") ||
    value.includes("liquidat") ||
    value.includes("administrator") ||
    value.includes("winding up") ||
    value.includes("receiver")
  );
};

const ResponsiveGridLayout = Responsive;

const formatPartialDob = (value?: string | null) => {
  if (!value) return "—";
  const [year, month] = value.split("-");
  if (!year) return value;
  if (!month) return year;
  const monthNum = Number(month);
  if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) return `${month}/${year}`;
  const date = new Date(Date.UTC(Number(year), monthNum - 1, 1));
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

const formatOfficerAddress = (officer: any) => {
  if (officer?.Full_Address) return officer.Full_Address;
  const parts = [
    officer?.Address_Line_1,
    officer?.Address_Line_2,
    officer?.Post_Town,
    officer?.County,
    officer?.Country,
    officer?.Person_Postcode,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "";
};

const formatPscAddress = (pscItem: any) => {
  if (pscItem?.Full_Address) return pscItem.Full_Address;
  const parts = [
    pscItem?.Data_Address_Address_Line_1,
    pscItem?.Data_Address_Address_Line_2,
    pscItem?.Data_Address_Locality,
    pscItem?.Data_Address_Region,
    pscItem?.Data_Address_Country,
    pscItem?.Data_Address_Postal_Code,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "";
};

const formatPscDob = (pscItem: any) => {
  const month = pscItem?.Data_Date_Of_Birth_Month;
  const year = pscItem?.Data_Date_Of_Birth_Year;
  if (!month && !year) return "";
  const monthString = month ? String(month).padStart(2, "0") : "";
  return `${monthString}/${year ?? ""}`.replace(/^\/|\/$/g, "");
};

function getStatusClasses(status: string): string {
  const s = status.toLowerCase();
  if (s === "active") return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200";
  if (s === "dissolved") return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200";
  return "bg-slate-100 text-slate-800 dark:bg-slate-800/60 dark:text-slate-200";
}

export function CompanyProfileClient({
  company,
  initialMode = false,
  showDebug = false,
  reportMode = false,
  initialHeaderColor = null,
}: CompanyProfileClientProps) {
  const router = useRouter();
  const [isMounted, setIsMounted] = React.useState(false);
  const isMobile = useMobile();
  const isDev = process.env.NODE_ENV === "development";
  const { resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const headerColorOptions = React.useMemo(
    () => [isDarkTheme ? "#ffffff" : "#000000", "#ff29e6", "#5ce1e6"],
    [isDarkTheme]
  );
  const [headerColor, setHeaderColor] = React.useState<string>(() => {
    if (initialHeaderColor) return initialHeaderColor;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("companyProfileHeaderColor");
      if (stored) return stored;
    }
    return "#ff29e6";
  });
  const [layoutEdit, setLayoutEdit] = React.useState(false);
  const [panelVisibility, setPanelVisibility] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(PROFILE_PANELS.map((panel) => [panel.key, true]))
  );
  const [gridLayouts, setGridLayouts] = React.useState<ResponsiveLayouts>({});
  
  // Backup states for canceling edit mode
  const [backupPanelVisibility, setBackupPanelVisibility] = React.useState<Record<string, boolean> | null>(null);
  const [backupGridLayouts, setBackupGridLayouts] = React.useState<ResponsiveLayouts | null>(null);
  const [backupHeaderColor, setBackupHeaderColor] = React.useState<string | null>(null);
  
  // Fullscreen state for entire profile
  const [isProfileMaximized, setIsProfileMaximized] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const { width: layoutWidth, containerRef: layoutWidthRef } =
    useContainerWidth({ measureBeforeMount: true });
  const layoutHeightRef = React.useRef<HTMLDivElement | null>(null);
  const [layoutHeight, setLayoutHeight] = React.useState(0);
  const [mapCoords, setMapCoords] = React.useState<{ lat: number; lon: number } | null>(
    isNumber(company.latitude) && isNumber(company.longitude) 
      ? { lat: company.latitude, lon: company.longitude } 
      : null
  );
  const [mapLoading, setMapLoading] = React.useState(true);
  const [mapError, setMapError] = React.useState(false);
  const [snapshot, setSnapshot] = React.useState<CompanySnapshot | null>(null);
  const [snapshotRaw, setSnapshotRaw] = React.useState<Record<string, unknown> | null>(null);
  const [snapshotLoading, setSnapshotLoading] = React.useState(false);
  const [snapshotError, setSnapshotError] = React.useState<string | null>(null);
  const [snapshotSource, setSnapshotSource] = React.useState<"api" | "cache" | "none">("none");
  const [officers, setOfficers] = React.useState<OfficerResponse | null>(null);
  const [officersLoading, setOfficersLoading] = React.useState(false);
  const [officersError, setOfficersError] = React.useState<string | null>(null);
  const [psc, setPsc] = React.useState<PscResponse | null>(null);
  const [pscLoading, setPscLoading] = React.useState(false);
  const [pscError, setPscError] = React.useState<string | null>(null);
  const [gazette, setGazette] = React.useState<GazetteResponse | null>(null);
  const [gazetteLoading, setGazetteLoading] = React.useState(false);
  const [gazetteError, setGazetteError] = React.useState<string | null>(null);
  const [peers, setPeers] = React.useState<PeersResponse | null>(null);
  const [peersLoading, setPeersLoading] = React.useState(false);
  const [peersError, setPeersError] = React.useState<string | null>(null);
  const [contacts, setContacts] = React.useState<CompanyContact[] | null>(null);
  const [contactsLoading, setContactsLoading] = React.useState(false);
  const [contactsError, setContactsError] = React.useState<string | null>(null);
  const [lseQuote, setLseQuote] = React.useState<LseQuote | null>(null);
  
  // Use global search mode
  const { isAdvanced: advancedMode, setIsAdvanced: setAdvancedMode } = useSearchMode();
  const { searchActive } = useSearchActive();

  // Sync with URL mode on initial load
  React.useEffect(() => {
    if (initialMode && !advancedMode) {
      setAdvancedMode(true);
    }
  }, [initialMode]);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    if (!isMounted) return;
    try {
      const stored = localStorage.getItem("companyProfileHeaderColor");
      if (stored) {
        setHeaderColor(stored);
      }
    } catch {
      // ignore storage errors
    }
  }, [isMounted]);

  React.useEffect(() => {
    if (!isMounted) return;
    const needsFlip =
      (isDarkTheme && headerColor === "#000000") ||
      (!isDarkTheme && headerColor === "#ffffff");
    if (!needsFlip) return;
    const nextColor = isDarkTheme ? "#ffffff" : "#000000";
    setHeaderColor(nextColor);
    try {
      localStorage.setItem("companyProfileHeaderColor", nextColor);
    } catch {
      // ignore storage errors
    }
  }, [headerColor, isDarkTheme, isMounted]);

  // Prevent body scroll when profile is maximized
  React.useEffect(() => {
    if (!isProfileMaximized) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isProfileMaximized]);

  React.useEffect(() => {
    if (!isMounted) return;
    try {
      const stored = localStorage.getItem(panelVisibilityStorageKey);
      if (!stored) return;
      const parsed = JSON.parse(stored) as Record<string, boolean> | null;
      if (!parsed) return;
      setPanelVisibility((prev) =>
        Object.fromEntries(
          PROFILE_PANELS.map((panel) => [panel.key, parsed[panel.key] ?? prev[panel.key] ?? true])
        )
      );
    } catch {
      // ignore storage errors
    }
  }, [isMounted]);

  React.useEffect(() => {
    if (!isMounted) return;
    try {
      localStorage.setItem(panelVisibilityStorageKey, JSON.stringify(panelVisibility));
    } catch {
      // ignore storage errors
    }
  }, [panelVisibility, isMounted]);

  React.useEffect(() => {
    const element = layoutHeightRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setLayoutHeight(entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleBackToSearch = () => {
    router.back();
  };

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      // Bust the ISR cache on the server for this specific page
      const slug = window.location.pathname.replace(/^\/company\//, "");
      await revalidateCompanyPage(slug);
      // Re-run server component + reload all client data
      router.refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const allowSnapshotCache = false;
  const enableOfficers = true;
  const enablePsc = true;
  const enableGazette = true;
  const enablePeers = true;

  React.useEffect(() => {
    if (reportMode || isMobile) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [reportMode, isMobile]);

  React.useEffect(() => {
    if (!reportMode) return;
    document.body.classList.add("company-report");
    return () => {
      document.body.classList.remove("company-report");
    };
  }, [reportMode]);

  React.useEffect(() => {
    let isMounted = true;
    const loadSnapshot = async () => {
      setSnapshotLoading(true);
      setSnapshotError(null);
      try {
        const response = await fetch(`/api/company/${company.company_number}`);
        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          const message = errorText || response.statusText || "Snapshot request failed";
          throw new Error(`Snapshot request failed (${response.status}): ${message}`);
        }
        const result = await response.json();
        if (!isMounted) return;
        const raw = result?.data ?? null;
        setSnapshotRaw(raw);
        const normalized = normalizeSnapshot(raw);
        setSnapshot(normalized);
        setSnapshotSource("api");
        if (!normalized) {
          setSnapshotError("Snapshot not found");
        }
      } catch (error) {
        if (isMounted) {
          setSnapshotError(error instanceof Error ? error.message : "Snapshot unavailable");
        }
      } finally {
        if (isMounted) {
          setSnapshotLoading(false);
        }
      }
    };
    loadSnapshot();
    return () => {
      isMounted = false;
    };
  }, [company.company_number]);

  React.useEffect(() => {
    if (!allowSnapshotCache) return;
    if (snapshotRaw || snapshot) return;
    try {
      const cached = sessionStorage.getItem(`companySnapshot:${company.company_number}`);
      if (!cached) return;
      const parsed = JSON.parse(cached) as { snapshot?: Record<string, unknown> } | null;
      if (!parsed?.snapshot) return;
      setSnapshotRaw(parsed.snapshot);
      const normalized = normalizeSnapshot(parsed.snapshot);
      setSnapshot(normalized);
      setSnapshotSource("cache");
    } catch {
      // ignore
    }
  }, [company.company_number, snapshotRaw, snapshot]);

  React.useEffect(() => {
    if (!enableOfficers) return;
    let isMounted = true;
    const loadOfficers = async () => {
      setOfficersLoading(true);
      setOfficersError(null);
      try {
        const response = await fetch(`/api/company/${company.company_number}/officers`);
        if (!response.ok) throw new Error("Officers request failed");
        const result = await response.json();
        if (!isMounted) return;
        setOfficers(result?.data ?? null);
      } catch {
        if (isMounted) setOfficersError("Officers unavailable");
      } finally {
        if (isMounted) setOfficersLoading(false);
      }
    };
    loadOfficers();
    return () => {
      isMounted = false;
    };
  }, [company.company_number]);

  React.useEffect(() => {
    if (!enablePsc) return;
    let isMounted = true;
    const loadPsc = async () => {
      setPscLoading(true);
      setPscError(null);
      try {
        const response = await fetch(`/api/company/${company.company_number}/psc`);
        if (!response.ok) throw new Error("PSC request failed");
        const result = await response.json();
        if (!isMounted) return;
        setPsc(result?.data ?? null);
      } catch {
        if (isMounted) setPscError("PSC unavailable");
      } finally {
        if (isMounted) setPscLoading(false);
      }
    };
    loadPsc();
    return () => {
      isMounted = false;
    };
  }, [company.company_number]);

  React.useEffect(() => {
    if (!enableGazette) return;
    let isMounted = true;
    const loadGazette = async () => {
      setGazetteLoading(true);
      setGazetteError(null);
      try {
        const response = await fetch(`/api/company/${company.company_number}/gazette?limit=25`);
        if (!response.ok) throw new Error("Gazette request failed");
        const result = await response.json();
        if (!isMounted) return;
        setGazette(result?.data ?? null);
      } catch {
        if (isMounted) setGazetteError("Gazette unavailable");
      } finally {
        if (isMounted) setGazetteLoading(false);
      }
    };
    loadGazette();
    return () => {
      isMounted = false;
    };
  }, [company.company_number]);

  React.useEffect(() => {
    if (!enablePeers) return;
    let isMounted = true;
    const loadPeers = async () => {
      setPeersLoading(true);
      setPeersError(null);
      try {
        const response = await fetch(`/api/company/${company.company_number}/peers`);
        if (!response.ok) throw new Error("Peers request failed");
        const result = await response.json();
        if (!isMounted) return;
        setPeers(result?.data ?? null);
      } catch {
        if (isMounted) setPeersError("Peers unavailable");
      } finally {
        if (isMounted) setPeersLoading(false);
      }
    };
    loadPeers();
    return () => {
      isMounted = false;
    };
  }, [company.company_number]);

  React.useEffect(() => {
    let isMounted = true;
    const loadContacts = async () => {
      setContactsLoading(true);
      setContactsError(null);
      try {
        const response = await fetch(`/api/company/${company.company_number}/contacts`);
        if (!response.ok) throw new Error("Contacts request failed");
        const result = await response.json();
        console.log('[CompanyProfileClient] Contacts API result:', result);
        console.log('[CompanyProfileClient] Extracted contacts:', result?.data?.data?.contacts);
        if (!isMounted) return;
        setContacts(result?.data?.data?.contacts ?? null);
      } catch (error) {
        console.error('[CompanyProfileClient] Contacts error:', error);
        if (isMounted) setContactsError("Contacts unavailable");
      } finally {
        if (isMounted) setContactsLoading(false);
      }
    };
    loadContacts();
    return () => {
      isMounted = false;
    };
  }, [company.company_number]);

  const resolvedCompanyName = snapshot?.Company_Name || company.company_name;
  const lseTicker = React.useMemo(() => getLseTicker(resolvedCompanyName), [resolvedCompanyName]);
  const lseShareName = resolvedCompanyName?.replace(/\s+PLC$/i, "").trim() || resolvedCompanyName;
  const lseLink = lseTicker
    ? `https://www.lse.co.uk/SharePrice.html?shareprice=${encodeURIComponent(
        lseTicker
      )}&share=${encodeURIComponent(lseShareName || lseTicker)}`
    : null;

  React.useEffect(() => {
    if (!lseTicker || reportMode) {
      setLseQuote(null);
      return;
    }
    let isActive = true;
    const loadQuote = async () => {
      try {
        const response = await fetch(`/api/lse-price?symbol=${encodeURIComponent(lseTicker)}`);
        if (!isActive) return;
        if (!response.ok || response.status === 204) {
          setLseQuote(null);
          return;
        }
        const result = await response.json();
        if (!isActive) return;
        setLseQuote(result?.data ?? null);
      } catch {
        if (isActive) setLseQuote(null);
      }
    };
    loadQuote();
    return () => {
      isActive = false;
    };
  }, [lseTicker, reportMode]);

  React.useEffect(() => {
    if (mapCoords) return;
    if (isNumber(snapshot?.Latitude) && isNumber(snapshot?.Longitude)) {
      setMapError(false);
      setMapLoading(true);
      setMapCoords({ lat: snapshot.Latitude, lon: snapshot.Longitude });
      return;
    }
    const looseCoords = getLooseLatLon(snapshotRaw);
    if (looseCoords) {
      setMapError(false);
      setMapLoading(true);
      setMapCoords(looseCoords);
    }
  }, [snapshot, mapCoords]);

  React.useEffect(() => {
    if (mapCoords) return;
    setMapLoading(false);
  }, [mapCoords]);

  React.useEffect(() => {
    if (!mapCoords) return;
    const timeoutId = window.setTimeout(() => {
      setMapLoading(false);
    }, 6000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mapCoords]);

  // Title case helper - handles ALL CAPS addresses nicely
  const toTitleCase = (str: string | undefined) => {
    if (!str) return str;
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  };

  const resolvedPostcode = snapshot?.Postcode ?? company.registered_address_postcode;
  const resolvedCounty = snapshot?.County ?? company.registered_address_county;
  const resolvedCountryRegion = snapshot?.Country_Region ?? "";
  const resolvedPostTown = snapshot?.Post_Town ?? company.registered_address_town;
  const resolvedAddressLine1 = toTitleCase(snapshot?.Address_Line_1 ?? company.registered_address_line_1);
  const resolvedAddressLine2 = toTitleCase(snapshot?.Address_Line_2 ?? company.registered_address_line_2);
  const displayPostTown = toTitleCase(resolvedPostTown);
  const displayCounty = toTitleCase(resolvedCounty);
  const displayCountryRegion = toTitleCase(resolvedCountryRegion);
  const shortAddress = [toTitleCase(resolvedPostTown), resolvedPostcode].filter(Boolean).join(", ");
  const fullAddress = [
    resolvedAddressLine1,
    resolvedAddressLine2,
    toTitleCase(resolvedPostTown), 
    toTitleCase(resolvedCounty), 
    toTitleCase(resolvedCountryRegion),
    resolvedPostcode  // Keep postcode uppercase
  ].filter(Boolean).join(", ");
  const mapSearchQuery = encodeURIComponent([company.company_name, fullAddress].filter(Boolean).join(", "));
  const mapEmbedUrl = mapCoords
    ? (() => {
        const lat = mapCoords.lat;
        const lon = mapCoords.lon;
        const delta = 0.01;
        const left = lon - delta;
        const right = lon + delta;
        const top = lat + delta;
        const bottom = lat - delta;
        return `https://www.openstreetmap.org/export/embed.html?bbox=${left}%2C${bottom}%2C${right}%2C${top}&layer=mapnik&marker=${lat}%2C${lon}`;
      })()
    : null;
  const staticMapUrls = mapCoords
    ? [
        `https://staticmap.openstreetmap.de/staticmap.php?center=${mapCoords.lat},${mapCoords.lon}&zoom=13&size=800x480&maptype=mapnik&markers=${mapCoords.lat},${mapCoords.lon},red-pushpin`,
        `https://maps.wikimedia.org/img/osm-intl,13,${mapCoords.lat},${mapCoords.lon},800x480.png`,
        `https://staticmap.openstreetmap.fr/staticmap.php?center=${mapCoords.lat},${mapCoords.lon}&zoom=13&size=800x480&markers=${mapCoords.lat},${mapCoords.lon},red-pushpin`,
      ]
    : null;

  const resolvedStatus = snapshot?.Company_Status ?? company.company_status;
  const resolvedType = snapshot?.Company_Type ?? company.company_type;
  const resolvedIncorporation = snapshot?.Incorporation_Date ?? company.incorporation_date;
  const resolvedDissolution = company.dissolution_date ?? null;
  const resolvedAgeBand = snapshot?.Age_Band ?? null;
  const resolvedAgeYears = snapshot?.Company_Age_Years ?? null;
  const resolvedCompaniesHouseLink = snapshot?.Companies_House_Link ?? `https://find-and-update.company-information.service.gov.uk/company/${company.company_number}`;
  const resolvedSector = snapshot?.Sector ?? company.sector;
  const resolvedIndustry = snapshot?.Industry ?? null;
  const resolvedDivision = snapshot?.Division ?? null;
  const resolvedSicCode1 = normalizeText(
    getRawSnapshotValue(
      snapshotRaw,
      "SIC_Code",
      "SIC_CODE",
      "SIC_Code_1",
      "SIC_CODE_1",
      "SIC",
      "sic_code",
      "sic_code_1"
    ) ?? company.sic_code_1
  );
  const resolvedSectionCode = snapshot?.Section_Code ?? null;
  const resolvedSicText1 = normalizeText(
    getRawSnapshotValue(
      snapshotRaw,
      "SIC_Text_1",
      "SIC_TEXT_1",
      "SIC_Text",
      "SIC_TEXT",
      "sic_text_1",
      "sic_text"
    ) ?? company.sic_text_1
  );
  const resolvedSicCode2 = normalizeText(
    getRawSnapshotValue(
      snapshotRaw,
      "SIC_Code_2",
      "SIC_CODE_2",
      "sic_code_2"
    ) ?? company.sic_code_2
  );
  const resolvedSicText2 = normalizeText(
    getRawSnapshotValue(
      snapshotRaw,
      "SIC_Text_2",
      "SIC_TEXT_2",
      "sic_text_2"
    ) ?? company.sic_text_2
  );
  const sic1Split = splitSic(resolvedSicCode1);
  const sic2Split = splitSic(resolvedSicCode2);
  const displaySicCode1 = sic1Split.code ?? resolvedSicCode1;
  const displaySicText1 = resolvedSicText1 ?? sic1Split.text;
  const displaySicCode2 = sic2Split.code ?? resolvedSicCode2;
  const displaySicText2 = resolvedSicText2 ?? sic2Split.text;
  const resolvedTurnover = snapshot?.Turnover_Year1 ?? company.latest_turnover;
  const resolvedEmployees = snapshot?.Employees_Year1 ?? company.latest_employees;
  const resolvedEmployeeBand = snapshot?.Employee_Band ?? null;
  const resolvedAssets = snapshot?.Total_Assets_Year1 ?? company.latest_total_assets;
  const resolvedProfit = snapshot?.Profit_Before_Tax_Year1 ?? company.latest_profit_before_tax;
  const resolvedAccountsDate = snapshot?.Year1_Period_End ?? snapshot?.Accounts_Date ?? company.latest_accounts_date;
  const resolvedLastUpdated = company.last_updated ?? null;
  const resolvedFilingSoftware = snapshot?.Filing_Software ?? null;
  const snapshotStatusLabel = snapshotLoading
    ? "Loading full data..."
    : snapshot
    ? "Accounts snapshot"
    : snapshotError
    ? "Snapshot unavailable"
    : null;
  const reliabilityLabel = snapshot?.is_reliable === 1 || snapshot?.is_reliable === true ? "Reliable" : snapshot?.is_reliable === 0 ? "Unverified" : null;
  const confidenceLabel = snapshot?.confidence_level ?? null;
  const redFlags = snapshot?.red_flag_count ?? null;
  const yearLabels = {
    year1: String(snapshot?.Year1_FY ?? "Year 1"),
    year2: String(snapshot?.Year2_FY ?? "Year 2"),
    year3: String(snapshot?.Year3_FY ?? "Year 3"),
  };
  const financialRows = [
    { label: "Turnover", y1: snapshot?.Turnover_Year1, y2: snapshot?.Turnover_Year2, y3: snapshot?.Turnover_Year3, format: formatMoney },
    { label: "Cost of Sales", y1: snapshot?.Cost_Of_Sales_Year1, y2: snapshot?.Cost_Of_Sales_Year2, y3: snapshot?.Cost_Of_Sales_Year3, format: formatMoney },
    { label: "Gross Profit", y1: snapshot?.Gross_Profit_Year1, y2: snapshot?.Gross_Profit_Year2, y3: snapshot?.Gross_Profit_Year3, format: formatMoney },
    { label: "Operating Profit", y1: snapshot?.Operating_Profit_Year1, y2: snapshot?.Operating_Profit_Year2, y3: snapshot?.Operating_Profit_Year3, format: formatMoney },
    { label: "Profit Before Tax", y1: snapshot?.Profit_Before_Tax_Year1, y2: snapshot?.Profit_Before_Tax_Year2, y3: snapshot?.Profit_Before_Tax_Year3, format: formatMoney },
    { label: "Profit After Tax", y1: snapshot?.Profit_After_Tax_Year1, y2: snapshot?.Profit_After_Tax_Year2, y3: snapshot?.Profit_After_Tax_Year3, format: formatMoney },
    { label: "Current Assets", y1: snapshot?.Current_Assets_Year1, y2: snapshot?.Current_Assets_Year2, y3: snapshot?.Current_Assets_Year3, format: formatMoney },
    { label: "Fixed Assets", y1: snapshot?.Fixed_Assets_Year1, y2: snapshot?.Fixed_Assets_Year2, y3: snapshot?.Fixed_Assets_Year3, format: formatMoney },
    { label: "Total Assets", y1: snapshot?.Total_Assets_Year1, y2: snapshot?.Total_Assets_Year2, y3: snapshot?.Total_Assets_Year3, format: formatMoney },
    { label: "Current Liabilities", y1: snapshot?.Current_Liabilities_Year1, y2: snapshot?.Current_Liabilities_Year2, y3: snapshot?.Current_Liabilities_Year3, format: formatMoney },
    { label: "Creditors >1 Year", y1: snapshot?.Creditors_After_One_Year_Year1, y2: snapshot?.Creditors_After_One_Year_Year2, y3: snapshot?.Creditors_After_One_Year_Year3, format: formatMoney },
    { label: "Total Liabilities", y1: snapshot?.Total_Liabilities_Year1, y2: snapshot?.Total_Liabilities_Year2, y3: snapshot?.Total_Liabilities_Year3, format: formatMoney },
    { label: "Total Equity", y1: snapshot?.Total_Equity_Year1, y2: snapshot?.Total_Equity_Year2, y3: snapshot?.Total_Equity_Year3, format: formatMoney },
    { label: "Cash", y1: snapshot?.Cash_Year1, y2: snapshot?.Cash_Year2, y3: snapshot?.Cash_Year3, format: formatMoney },
    { label: "Total Debt", y1: snapshot?.Total_Debt_Year1, y2: snapshot?.Total_Debt_Year2, y3: snapshot?.Total_Debt_Year3, format: formatMoney },
    { label: "Net Debt", y1: snapshot?.Net_Debt_Year1, y2: snapshot?.Net_Debt_Year2, y3: snapshot?.Net_Debt_Year3, format: formatMoney },
    { label: "Short-term Debt", y1: snapshot?.Short_Term_Debt_Year1, y2: snapshot?.Short_Term_Debt_Year2, y3: snapshot?.Short_Term_Debt_Year3, format: formatMoney },
    { label: "Long-term Debt", y1: snapshot?.Long_Term_Debt_Year1, y2: snapshot?.Long_Term_Debt_Year2, y3: snapshot?.Long_Term_Debt_Year3, format: formatMoney },
    { label: "Corp Tax Payable", y1: snapshot?.Corporation_Tax_Payable_Year1, y2: snapshot?.Corporation_Tax_Payable_Year2, y3: snapshot?.Corporation_Tax_Payable_Year3, format: formatMoney },
    { label: "Current Tax", y1: snapshot?.Current_Tax_For_Period_Year1, y2: snapshot?.Current_Tax_For_Period_Year2, y3: snapshot?.Current_Tax_For_Period_Year3, format: formatMoney },
    { label: "Effective Tax Rate", y1: snapshot?.Effective_Tax_Rate_Year1, y2: snapshot?.Effective_Tax_Rate_Year2, y3: snapshot?.Effective_Tax_Rate_Year3, format: formatPercent },
  ];
  const snapshotEntries = React.useMemo(() => {
    if (!snapshotRaw) return [];
    return Object.entries(snapshotRaw)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => [key, value ?? "—"] as const);
  }, [snapshotRaw]);

  const nextAccountsMadeUpTo = getRawSnapshotValue(
    snapshotRaw,
    "Next_Accounts_Made_Up_To",
    "Next_Accounts_Made_Up_To_Date",
    "Next_Accounts_Date"
  ) as string | null;
  const nextAccountsDue = getRawSnapshotValue(
    snapshotRaw,
    "Next_Accounts_Due",
    "Next_Accounts_Due_Date",
    "Accounts_Due"
  ) as string | null;
  const lastAccountsMadeUpTo = (getRawSnapshotValue(
    snapshotRaw,
    "Last_Accounts_Made_Up_To",
    "Last_Accounts_Made_Up_To_Date",
    "Accounts_Made_Up_To"
  ) as string | null) ?? (snapshot?.Year1_Period_End ?? snapshot?.Accounts_Date ?? null);
  const confirmationNextDate = getRawSnapshotValue(
    snapshotRaw,
    "Confirmation_Statement_Next_Date",
    "Next_Statement_Date",
    "Confirmation_Next_Date"
  ) as string | null;
  const confirmationDue = getRawSnapshotValue(
    snapshotRaw,
    "Confirmation_Statement_Due",
    "Confirmation_Statement_Due_By",
    "Next_Statement_Due",
    "Confirmation_Due"
  ) as string | null;
  const confirmationLastDate = getRawSnapshotValue(
    snapshotRaw,
    "Confirmation_Statement_Last_Date",
    "Last_Statement_Date",
    "Confirmation_Last_Date"
  ) as string | null;
  const previousNames = parsePreviousNames(
    getRawSnapshotValue(snapshotRaw, "Previous_Company_Names", "Previous_Company_Name", "Previous_Names")
  );
  
  // Clip paths for cutout vs full panel
  const cutoutClipPath = `polygon(
    0 16px, 4px 8px, 8px 4px, 16px 0,
    calc(100% - 16px) 0, calc(100% - 8px) 4px, calc(100% - 4px) 8px, 100% 16px,
    100% 100%,
    calc(50% + 430px) 100%, calc(50% + 428px) calc(100% - 3px), calc(50% + 424px) calc(100% - 8px),
    calc(50% + 418px) calc(100% - 15px), calc(50% + 408px) calc(100% - 28px), calc(50% + 390px) calc(100% - 48px),
    calc(50% + 365px) calc(100% - 68px), calc(50% + 335px) calc(100% - 82px), calc(50% + 300px) calc(100% - 88px),
    calc(50% - 300px) calc(100% - 88px), calc(50% - 335px) calc(100% - 82px), calc(50% - 365px) calc(100% - 68px),
    calc(50% - 390px) calc(100% - 48px), calc(50% - 408px) calc(100% - 28px), calc(50% - 418px) calc(100% - 15px),
    calc(50% - 424px) calc(100% - 8px), calc(50% - 428px) calc(100% - 3px), calc(50% - 430px) 100%,
    0 100%
  )`;
  
  const fullClipPath = `polygon(
    0 16px, 4px 8px, 8px 4px, 16px 0,
    calc(100% - 16px) 0, calc(100% - 8px) 4px, calc(100% - 4px) 8px, 100% 16px,
    100% 100%, 0 100%
  )`;

  // Both clip-paths must have the SAME number of points for smooth interpolation
  const innerCutoutClipPath = `polygon(
    0 12px, 3px 6px, 6px 3px, 12px 0,
    calc(100% - 12px) 0, calc(100% - 6px) 3px, calc(100% - 3px) 6px, 100% 12px,
    100% 100%,
    calc(50% + 418px) 100%, 
    calc(50% + 416px) calc(100% - 3px), 
    calc(50% + 412px) calc(100% - 8px),
    calc(50% + 406px) calc(100% - 15px), 
    calc(50% + 396px) calc(100% - 28px), 
    calc(50% + 378px) calc(100% - 48px),
    calc(50% + 353px) calc(100% - 68px), 
    calc(50% + 323px) calc(100% - 82px), 
    calc(50% + 288px) calc(100% - 88px),
    calc(50% - 288px) calc(100% - 88px), 
    calc(50% - 323px) calc(100% - 82px), 
    calc(50% - 353px) calc(100% - 68px),
    calc(50% - 378px) calc(100% - 48px), 
    calc(50% - 396px) calc(100% - 28px), 
    calc(50% - 406px) calc(100% - 15px),
    calc(50% - 412px) calc(100% - 8px), 
    calc(50% - 416px) calc(100% - 3px), 
    calc(50% - 418px) 100%,
    0 100%
  )`;
  
  // Same number of points, but all cutout points collapsed to bottom edge
  const innerFullClipPath = `polygon(
    0 12px, 3px 6px, 6px 3px, 12px 0,
    calc(100% - 12px) 0, calc(100% - 6px) 3px, calc(100% - 3px) 6px, 100% 12px,
    100% 100%,
    calc(50% + 418px) 100%, 
    calc(50% + 416px) 100%, 
    calc(50% + 412px) 100%,
    calc(50% + 406px) 100%, 
    calc(50% + 396px) 100%, 
    calc(50% + 378px) 100%,
    calc(50% + 353px) 100%, 
    calc(50% + 323px) 100%, 
    calc(50% + 288px) 100%,
    calc(50% - 288px) 100%, 
    calc(50% - 323px) 100%, 
    calc(50% - 353px) 100%,
    calc(50% - 378px) 100%, 
    calc(50% - 396px) 100%, 
    calc(50% - 406px) 100%,
    calc(50% - 412px) 100%, 
    calc(50% - 416px) 100%, 
    calc(50% - 418px) 100%,
    0 100%
  )`;

  const layoutStorageKey = "companyProfileLayout:rgl:v1";
  const panelVisibilityStorageKey = "companyProfilePanels:visibility:v1";
  const gridSize = 12;
  const gridColumns = 12;
  const gridRows = 6;
  const layoutBottomInset = 0;
  const gridMargin: [number, number] = [10, 10];
  const gridPadding: [number, number] = [0, 0];
  const layoutEdgePaddingX = 20;
  const layoutEdgePaddingTop = 0;
  const layoutEdgePaddingBottom = 20;
  const layoutRowNudge = 2;
  const layoutCanvasHeight = React.useMemo(
    () => Math.max(0, layoutHeight - layoutBottomInset),
    [layoutHeight, layoutBottomInset]
  );
  const layoutRowHeight = React.useMemo(() => {
    if (!layoutCanvasHeight) return 120;
    const availableHeight = Math.max(
      0,
      layoutCanvasHeight - gridPadding[1] * 2 - gridMargin[1] * (gridRows - 1) - layoutRowNudge
    );
    return Math.max(1, availableHeight / gridRows);
  }, [layoutCanvasHeight, gridRows, gridPadding, gridMargin, layoutRowNudge]);
  const layoutMaxRows = gridRows;
  const layoutColWidth = React.useMemo(() => {
    if (!layoutWidth) return 120;
    const availableWidth = Math.max(0, layoutWidth - gridPadding[0] * 2 - gridMargin[0] * (gridColumns - 1));
    return Math.max(1, availableWidth / gridColumns);
  }, [layoutWidth, gridPadding, gridMargin, gridColumns]);
  const layoutGridWidth = React.useMemo(
    () => layoutColWidth * gridColumns + gridMargin[0] * (gridColumns - 1),
    [layoutColWidth, gridColumns, gridMargin]
  );
  const layoutGridHeight = React.useMemo(
    () => layoutRowHeight * gridRows + gridMargin[1] * (gridRows - 1),
    [layoutRowHeight, gridRows, gridMargin]
  );
  const defaultLayouts = React.useMemo<ResponsiveLayouts>(() => {
    const baseLayout = [
      { i: "what", x: 0, y: 0, w: 2, h: 6, minW: 2, minH: 2, maxW: gridColumns, maxH: gridRows },
      { i: "where", x: 2, y: 0, w: 2, h: 6, minW: 2, minH: 2, maxW: gridColumns, maxH: gridRows },
      { i: "who", x: 4, y: 0, w: 2, h: 6, minW: 2, minH: 2, maxW: gridColumns, maxH: gridRows },
      { i: "how", x: 6, y: 0, w: 2, h: 6, minW: 2, minH: 2, maxW: gridColumns, maxH: gridRows },
      { i: "risks", x: 8, y: 0, w: 2, h: 6, minW: 2, minH: 2, maxW: gridColumns, maxH: gridRows },
      { i: "peers", x: 10, y: 0, w: 2, h: 6, minW: 2, minH: 2, maxW: gridColumns, maxH: gridRows },
    ];
    return {
      lg: baseLayout,
      md: baseLayout,
      sm: baseLayout,
      xs: baseLayout,
    };
  }, []);

  React.useEffect(() => {
    if (Object.keys(gridLayouts).length) return;
    try {
      const stored = localStorage.getItem(layoutStorageKey);
      if (stored) {
        setGridLayouts(JSON.parse(stored));
        return;
      }
    } catch {
      // ignore storage errors
    }
    setGridLayouts(defaultLayouts);
  }, [defaultLayouts, gridLayouts]);

  React.useEffect(() => {
    if (!Object.keys(gridLayouts).length) return;
    try {
      localStorage.setItem(layoutStorageKey, JSON.stringify(gridLayouts));
    } catch {
      // ignore storage errors
    }
  }, [gridLayouts, isDev, layoutEdit]);

  const handleHeaderColorChange = (color: string) => {
    setHeaderColor(color);
    try {
      localStorage.setItem("companyProfileHeaderColor", color);
    } catch {
      // ignore storage errors
    }
  };

  const clampLayouts = React.useCallback(
    (layouts: ResponsiveLayouts) => {
      const clampItems = (items: LayoutItem[] = []) =>
        items.map((item) => {
          const w = Math.min(item.w, gridColumns);
          const h = Math.min(item.h, gridRows);
          const x = Math.min(Math.max(item.x, 0), gridColumns - w);
          const y = Math.min(Math.max(item.y, 0), gridRows - h);
          return {
            ...item,
            w,
            h,
            x,
            y,
            minW: Math.max(item.minW ?? 2, 2),
            minH: Math.max(item.minH ?? 2, 2),
            maxW: gridColumns,
            maxH: gridRows,
          };
        });
      return Object.fromEntries(
        Object.entries(layouts || {}).map(([key, items]) => [key, clampItems(items as LayoutItem[])])
      ) as ResponsiveLayouts;
    },
    [gridColumns, gridRows]
  );

  const handleResetLayout = () => {
    setGridLayouts(defaultLayouts);
    try {
      localStorage.removeItem(layoutStorageKey);
    } catch {
      // ignore storage errors
    }
  };

  const handleSaveLayout = () => {
    if (!Object.keys(gridLayouts).length) return;
    try {
      localStorage.setItem(layoutStorageKey, JSON.stringify(gridLayouts));
      // Clear backups after successful save
      setBackupPanelVisibility(null);
      setBackupGridLayouts(null);
      setBackupHeaderColor(null);
    } catch {
      // ignore storage errors
    }
  };

  const handleExitLayout = () => {
    // Restore backup if available (user didn't save)
    if (backupPanelVisibility !== null) {
      setPanelVisibility(backupPanelVisibility);
    }
    if (backupGridLayouts !== null) {
      setGridLayouts(backupGridLayouts);
    }
    if (backupHeaderColor !== null) {
      setHeaderColor(backupHeaderColor);
    }
    // Clear backups
    setBackupPanelVisibility(null);
    setBackupGridLayouts(null);
    setBackupHeaderColor(null);
    // Exit edit mode
    setLayoutEdit(false);
  };

  const sanitizeFileName = (value: string) =>
    value.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim();

  const buildExportSections = (): ExportSection[] => {
    const safeFormatDate = (value?: string | null) =>
      value && !value.startsWith("0000-00-00") ? formatDate(value) : "";
    const isNullDate = (d?: string | null) => !d || d === "0000-00-00";
    const resignedRaw = officers?.resigned_officers ?? [];
    const effectivelyActive = resignedRaw.filter((o) => isNullDate(o.Resignation_Date));
    const activeOfficers = [...(officers?.active_officers ?? []), ...effectivelyActive];
    const activePscs = psc?.active_pscs ?? [];
    const notices = gazette?.notices ?? [];
    const peersList = peers?.results ?? [];

    const extractPscControls = (pscItem: any) => {
      const codes = [
        ...(Array.isArray(pscItem?.Natures_Of_Control) ? pscItem.Natures_Of_Control : []),
        pscItem?.Data_Natures_Of_Control_0_,
        pscItem?.Data_Natures_Of_Control_1_,
        pscItem?.Data_Natures_Of_Control_2_,
        pscItem?.Data_Natures_Of_Control_3_,
        pscItem?.Data_Natures_Of_Control_4_,
        pscItem?.Data_Natures_Of_Control_5_,
        pscItem?.Data_Natures_Of_Control_6_,
        pscItem?.Data_Natures_Of_Control_7_,
        pscItem?.Data_Natures_Of_Control_8_,
        pscItem?.Data_Natures_Of_Control_9_,
        pscItem?.Data_Natures_Of_Control_10_,
        pscItem?.Data_Natures_Of_Control_11_,
      ].filter(Boolean);
      return Array.from(new Set(codes)).join("; ");
    };

    const noticeTypes = gazette?.summary?.notice_types
      ? Object.entries(gazette.summary.notice_types)
          .map(([key, value]) => `${key} (${value})`)
          .join("; ")
      : "";

    return [
      {
        title: "What is it?",
        tables: [
          {
            name: "Overview",
            columns: ["Field", "Value"],
            rows: [
              ["Status", resolvedStatus],
              ["Type", resolvedType],
              ["Incorporated", safeFormatDate(resolvedIncorporation)],
              ["Age", isNumber(resolvedAgeYears) ? `${resolvedAgeYears.toFixed(1)} yrs` : ""],
              ["Dissolved", safeFormatDate(resolvedDissolution)],
              ["Sector", resolvedSector],
              ["Industry", resolvedIndustry],
              ["Division", resolvedDivision],
              ["Section", resolvedSectionCode],
              ["Employees band", resolvedEmployeeBand],
              ["Filing software", resolvedFilingSoftware],
              ["Last updated", safeFormatDate(resolvedLastUpdated)],
              ["SIC Code 1", displaySicCode1],
              ["SIC Text 1", displaySicText1],
              ["SIC Code 2", displaySicCode2],
              ["SIC Text 2", displaySicText2],
            ],
          },
        ],
      },
      {
        title: "Where is it?",
        tables: [
          {
            name: "Address",
            columns: ["Field", "Value"],
            rows: [
              ["Registered address", fullAddress],
              ["Address line 1", resolvedAddressLine1],
              ["Address line 2", resolvedAddressLine2],
              ["Post town", displayPostTown],
              ["County", displayCounty],
              ["Postcode", resolvedPostcode],
              ["Country / Region", displayCountryRegion],
              ["Latitude", mapCoords?.lat ?? ""],
              ["Longitude", mapCoords?.lon ?? ""],
            ],
          },
        ],
      },
      {
        title: "Who runs it?",
        tables: [
          {
            name: "Officers",
            columns: ["Name", "Role", "DOB", "Nationality", "Occupation", "Address", "Appointed", "Resigned"],
            rows: activeOfficers.map((officer) => [
              officer.Full_Name,
              officer.Appointment_Type,
              officer.Partial_Date_Of_Birth,
              officer.Nationality,
              officer.Occupation,
              formatOfficerAddress(officer),
              safeFormatDate(officer.Appointment_Date),
              safeFormatDate(officer.Resignation_Date),
            ]),
          },
          {
            name: "PSCs",
            columns: ["Name", "Kind", "DOB", "Nationality", "Residence", "Address", "Notified", "Ceased", "Controls"],
            rows: activePscs.map((item) => [
              item.Data_Name,
              item.Data_Kind,
              formatPscDob(item),
              item.Data_Nationality,
              item.Data_Country_Of_Residence,
              formatPscAddress(item),
              safeFormatDate(item.Data_Notified_On),
              safeFormatDate(item.Data_Ceased_On),
              extractPscControls(item),
            ]),
          },
        ],
      },
      {
        title: "How is it doing?",
        tables: [
          {
            name: "Financials",
            columns: ["Metric", yearLabels.year1, yearLabels.year2, yearLabels.year3],
            rows: financialRows.map((row) => [
              row.label,
              row.format(row.y1),
              row.format(row.y2),
              row.format(row.y3),
            ]),
          },
        ],
      },
      {
        title: "Any risks or official notices?",
        tables: [
          {
            name: "Summary",
            columns: ["Field", "Value"],
            rows: [
              ["Total notices", gazette?.summary?.total_notices ?? ""],
              ["Latest notice", safeFormatDate(gazette?.summary?.latest_notice_date ?? null)],
              ["Notice types", noticeTypes],
            ],
          },
          {
            name: "Notices",
            columns: ["Type", "Code", "Gazette date", "Last modified"],
            rows: notices.map((notice) => [
              notice.Notice_Type,
              notice.Notice_Code,
              safeFormatDate(notice.Gazette_Date),
              safeFormatDate(notice.Last_Modified_Date ?? null),
            ]),
          },
        ],
      },
      {
        title: "Who are its peers?",
        tables: [
          {
            name: "Peers",
            columns: ["Name", "Status", "Industry", "Post town", "Postcode", "Employees", "Turnover", "Assets"],
            rows: peersList.map((peer) => [
              peer.Company_Name,
              peer.Company_Status,
              peer.Industry,
              peer.Post_Town,
              peer.Postcode,
              peer.Employee_Band,
              peer.Turnover_Year1 ?? "",
              peer.Total_Assets_Year1 ?? "",
            ]),
          },
        ],
      },
    ];
  };

  const handleOpenReport = () => {
    if (typeof window === "undefined") return;
    const basePath = window.location.pathname.replace(/\/report$/, "");
    const colorParam = headerColor ? `?color=${encodeURIComponent(headerColor)}` : "";
    const reportUrl = `${basePath}/report${colorParam}`;
    window.open(reportUrl, "_blank", "noopener,noreferrer");
  };

  const handleExportCsv = () => {
    const sections = buildExportSections();
    const csv = buildCsv(sections);
    const nameBase = sanitizeFileName(`${snapshot?.Company_Name || company.company_name} ${company.company_number}`);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${nameBase}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    const sections = buildExportSections();
    const nameBase = sanitizeFileName(`${snapshot?.Company_Name || company.company_name} ${company.company_number}`);
    await exportToExcel(sections, nameBase);
  };

  const handleCopyExport = async () => {
    const sections = buildExportSections();
    const text = buildClipboardText(sections);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  const handleShowAllPanels = () => {
    setPanelVisibility(Object.fromEntries(PROFILE_PANELS.map((panel) => [panel.key, true])));
  };

  const handleHideAllPanels = () => {
    setPanelVisibility(Object.fromEntries(PROFILE_PANELS.map((panel) => [panel.key, false])));
  };

  const isExportReady =
    !mapLoading &&
    !mapError &&
    !snapshotLoading &&
    !officersLoading &&
    !pscLoading &&
    !gazetteLoading &&
    !peersLoading &&
    !contactsLoading;

  const visiblePanels = React.useMemo(
    () => PROFILE_PANELS.filter((panel) => panelVisibility[panel.key] !== false),
    [panelVisibility]
  );

  const renderPanel = (panel: PanelMeta, isReport = false) => {
    if (panel.key === "what") {
      return (
        <WhatPanel
          panel={panel}
          headerColor={headerColor}
          layoutEdit={layoutEdit}
          reportMode={isReport}
          resolvedStatus={resolvedStatus}
          resolvedType={resolvedType}
          resolvedIncorporation={resolvedIncorporation}
          resolvedDissolution={resolvedDissolution}
          resolvedSector={resolvedSector}
          resolvedIndustry={resolvedIndustry}
          resolvedDivision={resolvedDivision}
          resolvedSectionCode={resolvedSectionCode}
          resolvedEmployeeBand={resolvedEmployeeBand}
          resolvedPostTown={resolvedPostTown}
          resolvedCounty={resolvedCounty}
          resolvedPostcode={resolvedPostcode}
          resolvedCountryRegion={resolvedCountryRegion}
          resolvedLastUpdated={resolvedLastUpdated}
          resolvedFilingSoftware={resolvedFilingSoftware}
          displaySicCode1={displaySicCode1}
          displaySicText1={displaySicText1}
          displaySicCode2={displaySicCode2}
          displaySicText2={displaySicText2}
          formatDate={formatDate}
          formatAgeDetailed={formatAgeDetailed}
        />
      );
    }
    if (panel.key === "where") {
      return (
        <WherePanel
          panel={panel}
          headerColor={headerColor}
          layoutEdit={layoutEdit}
          reportMode={isReport}
          fullAddress={fullAddress}
          shortAddress={shortAddress}
          addressLine1={resolvedAddressLine1}
          addressLine2={resolvedAddressLine2}
          postTown={displayPostTown}
          county={displayCounty}
          postcode={resolvedPostcode ?? null}
          countryRegion={displayCountryRegion}
          mapCoords={mapCoords}
          mapLoading={mapLoading}
          mapError={mapError}
          mapEmbedUrl={mapEmbedUrl}
          staticMapUrls={staticMapUrls}
          mapSearchQuery={mapSearchQuery}
          setMapError={setMapError}
          setMapLoading={setMapLoading}
          contacts={contacts}
          contactsLoading={contactsLoading}
        />
      );
    }
    if (panel.key === "how") {
      return (
        <HowPanel
          panel={panel}
          headerColor={headerColor}
          layoutEdit={layoutEdit}
          reportMode={isReport}
          snapshot={snapshot}
          yearLabels={yearLabels}
          isMounted={isMounted}
          financialRows={financialRows}
        />
      );
    }
    if (panel.key === "risks") {
      return (
        <RisksPanel
          panel={panel}
          headerColor={headerColor}
          layoutEdit={layoutEdit}
          reportMode={isReport}
          gazette={gazette}
          gazetteLoading={gazetteLoading}
          gazetteError={gazetteError}
        />
      );
    }
    if (panel.key === "peers") {
      return (
        <PeersPanel
          panel={panel}
          headerColor={headerColor}
          layoutEdit={layoutEdit}
          reportMode={isReport}
          peers={peers}
          peersLoading={peersLoading}
          peersError={peersError}
        />
      );
    }
    return (
      <WhoPanel
        panel={panel}
        headerColor={headerColor}
        layoutEdit={layoutEdit}
        reportMode={isReport}
        officers={officers}
        officersLoading={officersLoading}
        officersError={officersError}
        psc={psc}
        pscLoading={pscLoading}
        pscError={pscError}
      />
    );
  };
  
  if (reportMode) {
    const reportCompanyName = snapshot?.Company_Name || company.company_name;
    const reportCompanyNumber = company.company_number;
    const reportStatus = resolvedStatus || company.company_status || "UK company";
    const reportType = resolvedType || "Company";
    const reportIncorporation = resolvedIncorporation ? formatDate(resolvedIncorporation) : null;
    const reportAge = isNumber(resolvedAgeYears) ? `${resolvedAgeYears.toFixed(1)} yrs` : null;
    const reportYear = new Date().getFullYear();
    const reportCopyright = `© ${reportYear} Crown copyright. Core data from Companies House, enhanced by Datadini.`;

    const panelLookup = Object.fromEntries(PROFILE_PANELS.map((panel) => [panel.key, panel]));
    const whoPanel = panelLookup.who;
    const howPanel = panelLookup.how;
    const risksPanel = panelLookup.risks;
    const peersPanel = panelLookup.peers;

    const buildOfficerResponse = (slice: Officer[]) => {
      if (!officers) {
        return {
          active_officers: slice,
          resigned_officers: [],
          summary: {
            total_officers: slice.length,
            active_directors: slice.length,
            resigned_directors: 0,
            company_secretaries: 0,
            corporate_directors: 0,
          },
        };
      }
      return { ...officers, active_officers: slice };
    };

    const buildPscResponse = (slice: Psc[]) => {
      if (!psc) {
        return {
          active_pscs: slice,
          ceased_pscs: [],
          summary: {
            total_pscs: slice.length,
            active_pscs: slice.length,
            ceased_pscs: 0,
            individual_pscs: 0,
            corporate_pscs: 0,
            sanctioned_pscs: 0,
          },
        };
      }
      return { ...psc, active_pscs: slice };
    };

    const buildGazetteResponse = (slice: GazetteNotice[]) =>
      gazette ? { ...gazette, notices: slice } : { notices: slice, summary: { total_notices: slice.length, latest_notice_date: null, notice_types: {} } };

    const buildPeersResponse = (slice: PeerCompany[]) =>
      peers ? { ...peers, results: slice, count: slice.length } : { results: slice, count: slice.length };

    const renderReportPage = ({
      pageKey,
      panel,
      content,
      hideSectionHeader = false,
      bodyClassName = "",
    }: {
      pageKey: string;
      panel: PanelMeta;
      content: React.ReactNode;
      hideSectionHeader?: boolean;
      bodyClassName?: string;
    }) => (
      <section key={pageKey} className="report-page">
        <div className="report-page-header">
          <DatadiniLogo className="h-6 w-auto" />
        </div>
        <div className="report-page-footer">{reportCopyright}</div>
        <div className={["report-page-body", bodyClassName].filter(Boolean).join(" ")}>
          {!hideSectionHeader && (
            <div className="report-section-header">
              <div className="report-section-kicker">{panel.key.toUpperCase()}</div>
              <h2 className="report-section-title">{panel.title}</h2>
              {panel.subtitle ? <p className="report-section-subtitle">{panel.subtitle}</p> : null}
            </div>
          )}
          <div className="report-panel">{content}</div>
        </div>
      </section>
    );

    const whoPages = (() => {
      const officerItems = officers?.active_officers ?? [];
      const pscItems = psc?.active_pscs ?? [];
      const pages: Array<{ officers: Officer[]; pscs: Psc[] }> = [];
      if (officerItems.length === 0 && pscItems.length === 0) {
        return [{ officers: [], pscs: [] }];
      }

      if (officerItems.length > 0) {
        pages.push({ officers: officerItems.slice(0, 2), pscs: [] });
        for (let i = 2; i < officerItems.length; i += 3) {
          pages.push({ officers: officerItems.slice(i, i + 3), pscs: [] });
        }
      }

      if (pscItems.length > 0) {
        const startIndex = pages.length === 0 ? 0 : 1;
        pages.push({ officers: [], pscs: pscItems.slice(0, startIndex === 0 ? 1 : 2) });
        for (let i = startIndex === 0 ? 1 : 2; i < pscItems.length; i += 2) {
          pages.push({ officers: [], pscs: pscItems.slice(i, i + 2) });
        }
      }

      return pages;
    })();

    const risksPages = (() => {
      const notices = gazette?.notices ?? [];
      if (notices.length === 0) return [null];
      const chunks: GazetteNotice[][] = [];
      for (let i = 0; i < notices.length; i += 15) {
        chunks.push(notices.slice(i, i + 15));
      }
      return chunks;
    })();

    const peersPages = (() => {
      const results = peers?.results ?? [];
      if (results.length === 0) return [null];
      const chunks: PeerCompany[][] = [];
      for (let i = 0; i < results.length; i += 3) {
        chunks.push(results.slice(i, i + 3));
      }
      return chunks;
    })();

    return (
      <div className="report-root">
        <div className="report-toolbar report-hide">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="text-xs"
              disabled={mapLoading}
              title={mapLoading ? "Waiting for the map to load" : undefined}
            >
              Print / Save PDF
            </Button>
            {mapLoading ? (
              <span className="text-[10px] text-muted-foreground">Waiting for map…</span>
            ) : mapError ? (
              <span className="text-[10px] text-amber-600">Map failed to load</span>
            ) : null}
          </div>
        </div>
        <div className="report-print-header">
          <DatadiniLogo className="h-6 w-auto" />
        </div>
        <div className="report-print-footer">{reportCopyright}</div>

        <section className="report-page report-cover">
          <div className="report-page-header">
            <DatadiniLogo className="h-6 w-auto" />
          </div>
          <div className="report-page-footer">{reportCopyright}</div>
          <div className="report-page-body">
            <div className="report-hero">
              <div className="report-hero-icon">
                <DatadiniIcon />
              </div>
              <div className="report-hero-logo">
                <DatadiniHighlightLogo
                  className="h-12 w-auto align-middle"
                  colors={{
                    D1: "#ff29e6",
                    A1: "#ff29e6",
                    T: "#ff29e6",
                    A2: "#ff29e6",
                    D2: "#ff29e6",
                    I1: "#ff29e6",
                    N: "#ff29e6",
                    I2: "#ff29e6",
                  }}
                />
              </div>
              <h1 className="report-title">AI-Powered UK Business Intelligence</h1>
              <p className="report-subtitle">Thoughtfully Curated for Data-Driven Decisions</p>
            </div>
            <div className="report-company">
              <h2 className="report-company-name">{reportCompanyName}</h2>
              <div className="report-company-meta">
                <span>{reportCompanyNumber}</span>
                <span>· {reportType}</span>
                <span>· {reportStatus}</span>
                {reportIncorporation ? <span>· Est. {reportIncorporation}</span> : null}
                {reportAge ? <span>· Age {reportAge}</span> : null}
              </div>
            </div>
          </div>
        </section>

        <section className="report-page report-toc">
          <div className="report-page-header">
            <DatadiniLogo className="h-6 w-auto" />
          </div>
          <div className="report-page-footer">{reportCopyright}</div>
          <div className="report-page-body">
            <h2 className="report-section-title">Table of Contents</h2>
            <ol className="report-toc-list">
              {PROFILE_PANELS.map((panel) => (
                <li key={panel.key}>
                  <div className="report-toc-title">{panel.title}</div>
                  {panel.subtitle ? <div className="report-toc-subtitle">{panel.subtitle}</div> : null}
                </li>
              ))}
            </ol>
          </div>
        </section>

        {PROFILE_PANELS.flatMap((panel) => {
          if (panel.key === "how") {
            return [
              renderReportPage({
                pageKey: "how-table",
                panel: howPanel,
                content: (
                  <HowPanel
                    panel={howPanel}
                    headerColor={headerColor}
                    layoutEdit={layoutEdit}
                    reportMode
                    reportView="table"
                    reportHideSummary
                    snapshot={snapshot}
                    yearLabels={yearLabels}
                    isMounted={isMounted}
                    financialRows={financialRows}
                  />
                ),
              }),
              renderReportPage({
                pageKey: "how-chart",
                panel: howPanel,
                content: (
                  <HowPanel
                    panel={howPanel}
                    headerColor={headerColor}
                    layoutEdit={layoutEdit}
                    reportMode
                    reportView="chart"
                    reportHideSummary
                    snapshot={snapshot}
                    yearLabels={yearLabels}
                    isMounted={isMounted}
                    financialRows={financialRows}
                  />
                ),
                bodyClassName: "pt-4",
              }),
            ];
          }
          if (panel.key === "who") {
            return whoPages.map((page, index) =>
              renderReportPage({
                pageKey: `who-${index}`,
                panel: whoPanel,
                content: (
                  <WhoPanel
                    panel={whoPanel}
                    headerColor={headerColor}
                    layoutEdit={layoutEdit}
                    reportMode
                    reportHideSummary={index > 0}
                    officers={buildOfficerResponse(page.officers)}
                    officersLoading={officersLoading}
                    officersError={officersError}
                    psc={buildPscResponse(page.pscs)}
                    pscLoading={pscLoading}
                    pscError={pscError}
                  />
                ),
                hideSectionHeader: index > 0,
                bodyClassName: index > 0 ? "pt-4" : "",
              })
            );
          }
          if (panel.key === "risks") {
            return risksPages.map((chunk, index) =>
              renderReportPage({
                pageKey: `risks-${index}`,
                panel: risksPanel,
                content: (
                  <RisksPanel
                    panel={risksPanel}
                    headerColor={headerColor}
                    layoutEdit={layoutEdit}
                    reportMode
                    reportHideSummary={index > 0}
                    gazette={chunk ? buildGazetteResponse(chunk) : gazette}
                    gazetteLoading={gazetteLoading}
                    gazetteError={gazetteError}
                  />
                ),
                hideSectionHeader: index > 0,
                bodyClassName: index > 0 ? "pt-4" : "",
              })
            );
          }
          if (panel.key === "peers") {
            return peersPages.map((chunk, index) =>
              renderReportPage({
                pageKey: `peers-${index}`,
                panel: peersPanel,
                content: (
                  <PeersPanel
                    panel={peersPanel}
                    headerColor={headerColor}
                    layoutEdit={layoutEdit}
                    reportMode
                    peers={chunk ? buildPeersResponse(chunk) : peers}
                    peersLoading={peersLoading}
                    peersError={peersError}
                  />
                ),
                hideSectionHeader: index > 0,
                bodyClassName: index > 0 ? "pt-4" : "",
              })
            );
          }
          return [
            renderReportPage({
              pageKey: panel.key,
              panel,
              content: renderPanel(panel, true),
            }),
          ];
        })}

        <section className="report-page report-thanks">
          <div className="report-page-header">
            <DatadiniLogo className="h-6 w-auto" />
          </div>
          <div className="report-page-footer">{reportCopyright}</div>
          <div className="report-page-body">
            <div className="report-thanks">
              <div className="report-hero-icon">
                <DatadiniIcon />
              </div>
              <h2 className="report-title">Thank you</h2>
              <p className="report-subtitle">We appreciate you exploring this report.</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ─── MOBILE VIEW (early return — desktop code below is untouched) ─────────
  if (isMounted && isMobile) {
    const isNullDate = (d?: string | null) => !d || d === "0000-00-00";
    const mobileOfficers = [
      ...(officers?.active_officers ?? []),
      ...(officers?.resigned_officers ?? []).filter((o: any) => isNullDate(o.Resignation_Date)),
    ];
    const mobilePscs = psc?.active_pscs ?? [];

    return (
      <>
        <div className="fixed inset-0 app-gradient-bg" />
        <div className="fixed top-[5vh] inset-x-0 bottom-0 overflow-y-auto pb-24 z-10">

          {/* ── Sticky header ── */}
          <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b px-4 py-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <button
                onClick={() => router.back()}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <a
                href={resolvedCompaniesHouseLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-muted-foreground border border-border rounded px-2 py-1 hover:bg-muted/50 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                Companies House
              </a>
            </div>
            <h1 className="text-lg font-bold text-foreground leading-tight">{resolvedCompanyName}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="font-mono text-xs text-muted-foreground">{company.company_number}</span>
              {resolvedStatus && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusClasses(resolvedStatus)}`}>
                  {resolvedStatus}
                </span>
              )}
              {resolvedType && <span className="text-xs text-muted-foreground">{resolvedType}</span>}
            </div>
          </div>

          {/* ── Content panels ── */}
          <div className="px-4 py-4 space-y-3">

            {/* Company Info */}
            <section className="rounded-xl border bg-card p-4">
              <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>Company Info</h2>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                {resolvedIncorporation && (<><dt className="text-muted-foreground text-xs">Incorporated</dt><dd className="font-medium text-xs">{formatDate(resolvedIncorporation)}</dd></>)}
                {resolvedDissolution && (<><dt className="text-muted-foreground text-xs">Dissolved</dt><dd className="font-medium text-xs text-red-600">{formatDate(resolvedDissolution)}</dd></>)}
                {resolvedType && (<><dt className="text-muted-foreground text-xs">Type</dt><dd className="font-medium text-xs">{resolvedType}</dd></>)}
                {resolvedSector && (<><dt className="text-muted-foreground text-xs">Sector</dt><dd className="font-medium text-xs leading-snug">{resolvedSector}</dd></>)}
                {displaySicCode1 && (<><dt className="text-muted-foreground text-xs">SIC 1</dt><dd className="font-medium text-xs">{displaySicCode1}{displaySicText1 ? ` · ${displaySicText1}` : ""}</dd></>)}
                {displaySicCode2 && (<><dt className="text-muted-foreground text-xs">SIC 2</dt><dd className="font-medium text-xs">{displaySicCode2}{displaySicText2 ? ` · ${displaySicText2}` : ""}</dd></>)}
              </dl>
            </section>

            {/* Address */}
            {fullAddress && (
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold mb-2" style={{ color: headerColor }}>Registered Office</h2>
                <p className="text-sm text-foreground leading-relaxed mb-3">{fullAddress}</p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  {resolvedAddressLine1 && (<><dt className="text-muted-foreground">Line 1</dt><dd className="font-medium">{resolvedAddressLine1}</dd></>)}
                  {resolvedAddressLine2 && (<><dt className="text-muted-foreground">Line 2</dt><dd className="font-medium">{resolvedAddressLine2}</dd></>)}
                  {displayPostTown && (<><dt className="text-muted-foreground">Town</dt><dd className="font-medium">{displayPostTown}</dd></>)}
                  {displayCounty && (<><dt className="text-muted-foreground">County</dt><dd className="font-medium">{displayCounty}</dd></>)}
                  {resolvedPostcode && (<><dt className="text-muted-foreground">Postcode</dt><dd className="font-medium">{resolvedPostcode}</dd></>)}
                  {displayCountryRegion && (<><dt className="text-muted-foreground">Country</dt><dd className="font-medium">{displayCountryRegion}</dd></>)}
                </dl>
                {mapCoords && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${mapCoords.lat},${mapCoords.lon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-600 hover:underline"
                  >
                    <MapPin className="h-3 w-3" />
                    View on Google Maps
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </section>
            )}

            {/* Contact details */}
            {(contactsLoading || (contacts && contacts.length > 0)) && (
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>Contact</h2>
                {contactsLoading ? (
                  <p className="text-xs text-muted-foreground">Loading…</p>
                ) : (() => {
                  const c = contacts![0];
                  const websiteUrl = c.Website || (c.Domain ? `https://${c.Domain}` : null);
                  return (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
                      {websiteUrl && (<><dt className="text-muted-foreground">Website</dt><dd className="font-medium break-all"><a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{c.Website || c.Domain}</a></dd></>)}
                      {c.Email && (<><dt className="text-muted-foreground">Email</dt><dd className="font-medium break-all"><a href={`mailto:${c.Email}`} className="text-blue-600 hover:underline">{c.Email}</a></dd></>)}
                      {c.Phone && (<><dt className="text-muted-foreground">Phone</dt><dd className="font-medium"><a href={`tel:${c.Phone}`} className="text-blue-600 hover:underline">{c.Phone}</a></dd></>)}
                      {c.LinkedIn && (<><dt className="text-muted-foreground">LinkedIn</dt><dd className="font-medium break-all"><a href={c.LinkedIn} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{c.LinkedIn.replace(/^https?:\/\/(www\.)?/, "")}</a></dd></>)}
                      {c.Twitter && (<><dt className="text-muted-foreground">X / Twitter</dt><dd className="font-medium break-all"><a href={c.Twitter} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{c.Twitter.replace(/^https?:\/\/(www\.)?/, "")}</a></dd></>)}
                    </dl>
                  );
                })()}
              </section>
            )}

            {/* Directors & Officers */}
            <section className="rounded-xl border bg-card p-4">
              <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>
                Directors &amp; Officers{officersLoading ? "" : ` (${mobileOfficers.length})`}
              </h2>
              {officersLoading ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : mobileOfficers.length === 0 ? (
                <p className="text-xs text-muted-foreground">No active officers found</p>
              ) : (
                <div className="divide-y divide-border/50">
                  {mobileOfficers.map((o: any, i: number) => {
                    const nameSlug = (o.Full_Name ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").substring(0, 60);
                    return (
                      <Link
                        key={i}
                        href={`/people/${nameSlug}`}
                        prefetch={false}
                        className="py-2.5 flex items-start justify-between gap-2 hover:opacity-80 transition-opacity block"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">{o.Full_Name}</p>
                          <p className="text-xs text-muted-foreground">{o.Appointment_Type}</p>
                          {o.Nationality && <p className="text-xs text-muted-foreground">{o.Nationality}</p>}
                        </div>
                        {o.Appointment_Date && (
                          <p className="text-xs text-muted-foreground whitespace-nowrap shrink-0 mt-0.5">{formatDate(o.Appointment_Date)}</p>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
              {/* Former officers */}
              {!officersLoading && (() => {
                const formerOfficers = (officers?.resigned_officers ?? []).filter(
                  (o: any) => o.Resignation_Date && o.Resignation_Date !== "0000-00-00"
                );
                if (formerOfficers.length === 0) return null;
                return (
                  <details className="mt-3">
                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                      {formerOfficers.length} former officer{formerOfficers.length !== 1 ? "s" : ""}
                    </summary>
                    <div className="mt-2 divide-y divide-border/50">
                      {formerOfficers.slice(0, 20).map((o: any, i: number) => (
                        <div key={i} className="py-2 opacity-60">
                          <p className="text-xs font-medium">{o.Full_Name}</p>
                          <div className="flex gap-2 text-xs text-muted-foreground">
                            <span>{o.Appointment_Type}</span>
                            {o.Resignation_Date && <span>· Resigned {formatDate(o.Resignation_Date)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })()}
            </section>

            {/* People with Significant Control */}
            {(mobilePscs.length > 0 || pscLoading) && (
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>
                  People with Significant Control{pscLoading ? "" : ` (${mobilePscs.length})`}
                </h2>
                {pscLoading ? (
                  <p className="text-xs text-muted-foreground">Loading…</p>
                ) : (
                  <div className="divide-y divide-border/50">
                    {mobilePscs.map((item: any, i: number) => (
                      <div key={i} className="py-2.5">
                        <p className="text-sm font-medium">{item.Data_Name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{item.Data_Kind}</p>
                        {item.Appointment_Date && (
                          <p className="text-xs text-muted-foreground">Appointed {formatDate(item.Appointment_Date)}</p>
                        )}
                        {item.Nationality && (
                          <p className="text-xs text-muted-foreground">{item.Nationality}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Financial Highlights - multi-year */}
            {(resolvedTurnover != null || resolvedAssets != null || resolvedProfit != null || snapshot?.Turnover_Year2 != null || snapshot?.Turnover_Year3 != null) && (
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>Financials</h2>
                {/* Year labels */}
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 text-xs mb-2">
                  <span />
                  {snapshot?.Year1_Period_End && <span className="text-muted-foreground text-right">{new Date(snapshot.Year1_Period_End).getFullYear()}</span>}
                  {snapshot?.Year2_Period_End && <span className="text-muted-foreground text-right">{new Date(snapshot.Year2_Period_End).getFullYear()}</span>}
                  {snapshot?.Year3_Period_End && <span className="text-muted-foreground text-right">{new Date(snapshot.Year3_Period_End).getFullYear()}</span>}
                </div>
                <div className="space-y-2 text-xs">
                  {[
                    { label: "Turnover", y1: snapshot?.Turnover_Year1 ?? resolvedTurnover, y2: snapshot?.Turnover_Year2, y3: snapshot?.Turnover_Year3 },
                    { label: "Gross Profit", y1: snapshot?.Gross_Profit_Year1, y2: snapshot?.Gross_Profit_Year2, y3: snapshot?.Gross_Profit_Year3 },
                    { label: "Profit before Tax", y1: snapshot?.Profit_Before_Tax_Year1 ?? resolvedProfit, y2: snapshot?.Profit_Before_Tax_Year2, y3: snapshot?.Profit_Before_Tax_Year3 },
                    { label: "Total Assets", y1: snapshot?.Total_Assets_Year1 ?? resolvedAssets, y2: snapshot?.Total_Assets_Year2, y3: snapshot?.Total_Assets_Year3 },
                    { label: "Cash", y1: snapshot?.Cash_Year1, y2: snapshot?.Cash_Year2, y3: snapshot?.Cash_Year3 },
                    { label: "Employees", y1: snapshot?.Employees_Year1 ?? resolvedEmployees, y2: snapshot?.Employees_Year2, y3: snapshot?.Employees_Year3, isCount: true },
                  ].filter(row => row.y1 != null || row.y2 != null || row.y3 != null).map(({ label, y1, y2, y3, isCount }: any) => (
                    <div key={label} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 items-baseline py-1.5 border-b border-border/40 last:border-0">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium text-right tabular-nums">{y1 != null ? (isCount ? y1.toLocaleString() : formatCurrency(y1)) : "—"}</span>
                      {snapshot?.Year2_Period_End && <span className="font-medium text-right tabular-nums text-muted-foreground">{y2 != null ? (isCount ? y2.toLocaleString() : formatCurrency(y2)) : "—"}</span>}
                      {snapshot?.Year3_Period_End && <span className="font-medium text-right tabular-nums text-muted-foreground">{y3 != null ? (isCount ? y3.toLocaleString() : formatCurrency(y3)) : "—"}</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Filing dates */}
            {(nextAccountsDue || nextAccountsMadeUpTo || lastAccountsMadeUpTo || confirmationNextDate || confirmationDue || confirmationLastDate) && (
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>Filing Dates</h2>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
                  {nextAccountsDue && (<><dt className="text-muted-foreground">Accounts due</dt><dd className="font-medium">{formatDate(nextAccountsDue)}</dd></>)}
                  {nextAccountsMadeUpTo && (<><dt className="text-muted-foreground">Accounts made up to</dt><dd className="font-medium">{formatDate(nextAccountsMadeUpTo)}</dd></>)}
                  {lastAccountsMadeUpTo && (<><dt className="text-muted-foreground">Last accounts</dt><dd className="font-medium">{formatDate(lastAccountsMadeUpTo)}</dd></>)}
                  {confirmationNextDate && (<><dt className="text-muted-foreground">Confirmation due</dt><dd className="font-medium">{formatDate(confirmationNextDate)}</dd></>)}
                  {confirmationLastDate && (<><dt className="text-muted-foreground">Last confirmation</dt><dd className="font-medium">{formatDate(confirmationLastDate)}</dd></>)}
                </dl>
              </section>
            )}

            {/* Gazette notices */}
            {!gazetteLoading && gazette && gazette.notices.length > 0 && (
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>
                  Gazette Notices ({gazette.summary.total_notices})
                </h2>
                <div className="space-y-2">
                  {gazette.notices.slice(0, 5).map((n) => (
                    <div key={n.Hash_Key} className="text-xs border-l-2 border-amber-400 pl-3 py-0.5">
                      <p className="font-medium">{n.Notice_Type}</p>
                      <p className="text-muted-foreground">{formatDate(n.Gazette_Date)}</p>
                    </div>
                  ))}
                  {gazette.notices.length > 5 && (
                    <p className="text-xs text-muted-foreground">+{gazette.notices.length - 5} more notices</p>
                  )}
                </div>
              </section>
            )}

            {/* Similar companies */}
            {!peersLoading && peers && peers.results.length > 0 && (
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>
                  Similar Companies
                </h2>
                <div className="divide-y divide-border/50">
                  {peers.results.slice(0, 8).map((p) => {
                    const peerSlug = `${p.Company_Number}-${p.Company_Name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").substring(0, 50)}`;
                    return (
                      <Link
                        key={p.Company_Number}
                        href={`/company/${peerSlug}`}
                        prefetch={false}
                        className="py-2.5 flex items-start justify-between gap-2 hover:opacity-80 transition-opacity block"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{p.Company_Name}</p>
                          <div className="flex flex-wrap gap-x-2 text-xs text-muted-foreground mt-0.5">
                            {p.Post_Town && <span>{p.Post_Town}</span>}
                            {p.Employee_Band && <span>· {p.Employee_Band} employees</span>}
                          </div>
                        </div>
                        {p.Turnover_Year1 != null && (
                          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap shrink-0 mt-0.5">{formatCurrency(p.Turnover_Year1)}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Previous names */}
            {previousNames && previousNames.length > 0 && (
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold mb-3" style={{ color: headerColor }}>Previous Names</h2>
                <div className="space-y-1">
                  {previousNames.map((n: any, i: number) => (
                    <div key={i} className="flex items-start justify-between gap-2 text-xs">
                      <span className="font-medium">{n.name ?? n}</span>
                      {n.ceased_on && <span className="text-muted-foreground whitespace-nowrap">{formatDate(n.ceased_on)}</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Refresh */}
            <button
              onClick={async () => { await revalidateCompanyPage(company.company_number); router.refresh(); }}
              className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground border border-border rounded-xl py-3 hover:bg-muted/40 transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh data
            </button>

          </div>
        </div>
      </>
    );
  }
  // ─── END MOBILE VIEW ────────────────────────────────────────────────────────

  return (
    <>
      {/* Gradient background */}
      <div className="fixed inset-0 app-gradient-bg" />
      
      {/* Profile panel - with padding and cutout around search pill */}
      <motion.div 
        className={`fixed top-16 bottom-9 left-4 right-4 overflow-hidden ${isProfileMaximized ? "opacity-0 pointer-events-none" : ""}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <motion.div 
          className="w-full h-full bg-background/70 backdrop-blur-xl rounded-t-2xl p-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{
            clipPath: fullClipPath
          }}
        >
          {/* Inner white panel - cutout only in simple mode when search is active */}
          <div 
            className="h-full rounded-xl bg-card flex flex-col overflow-hidden"
            style={{
              clipPath: (isMounted && searchActive && !advancedMode) ? innerCutoutClipPath : innerFullClipPath,
              transition: 'clip-path 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
              
              {/* Header */}
              <div className="px-5 py-3 flex items-start justify-between gap-4 shrink-0" suppressHydrationWarning>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex items-center gap-x-3 min-w-0 overflow-hidden text-[12px] sm:text-[13px] text-muted-foreground">
                      <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
                        {resolvedCompanyName}
                      </h1>
                      {/* Fixed space for company logo/favicon */}
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shrink-0" suppressHydrationWarning>
                        {contacts && contacts.length > 0 && (contacts[0].Website || contacts[0].Domain) && (
                          <img
                            src={`https://${contacts[0].Domain || new URL(contacts[0].Website!).hostname}/apple-touch-icon.png`}
                            alt="Company logo"
                            className="w-5 h-5 sm:w-6 sm:h-6 object-contain hidden"
                            onLoad={(e) => {
                              e.currentTarget.classList.remove('hidden');
                            }}
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.classList.add('hidden');
                              
                              if (target.src.includes('apple-touch-icon.png')) {
                                target.src = target.src.replace('apple-touch-icon.png', 'favicon-32x32.png');
                              } else if (target.src.includes('favicon-32x32.png')) {
                                target.src = target.src.replace('favicon-32x32.png', 'favicon.ico');
                              }
                            }}
                          />
                        )}
                      </div>
                      <span className="font-mono whitespace-nowrap">
                        {company.company_number}
                      </span>
                      {resolvedType && (
                        <span className="whitespace-nowrap">· {resolvedType}</span>
                      )}
                      {resolvedIncorporation && (
                        <span className="whitespace-nowrap">
                          · Est. {formatDate(resolvedIncorporation)}
                        </span>
                      )}
                      {isNumber(resolvedAgeYears) && (
                        <span className="whitespace-nowrap">
                          · Age {resolvedAgeYears.toFixed(1)} yrs
                        </span>
                      )}
                      {lseQuote && (
                        <span className="whitespace-nowrap">
                          ·{" "}
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            Share price
                          </span>{" "}
                          <span className="relative font-semibold text-foreground">
                            <span className="mr-1 text-[10px] font-medium text-muted-foreground">
                              GBX
                            </span>
                            {formatStockPrice(lseQuote.price)}
                            <span
                              className={`absolute -top-2 left-full ml-1 text-[9px] ${
                                lseQuote.change > 0
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : lseQuote.change < 0
                                  ? "text-rose-600 dark:text-rose-400"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {formatStockChange(lseQuote.change, lseQuote.changePercent)}
                            </span>
                          </span>
                        </span>
                      )}
                    </div>
                    {lseLink && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="ml-3 h-7 text-[10px] shrink-0"
                        data-export-hide="true"
                      >
                        <a href={lseLink} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3 w-3 mr-1" />LSE
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      className={`${lseLink ? "" : "ml-3 "}h-7 text-[10px] shrink-0`}
                      data-export-hide="true"
                    >
                      <a href={resolvedCompaniesHouseLink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3 w-3 mr-1" />Companies House
                      </a>
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0" data-export-hide="true">
                  {/* Refresh data button — busts ISR cache and reloads all panel data */}
                  {!layoutEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshData}
                      disabled={isRefreshing}
                      className="text-xs"
                      title="Force a fresh data pull from Companies House and clear the page cache"
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${isRefreshing ? "animate-spin" : ""}`} />
                      {isRefreshing ? "Refreshing…" : "Refresh data"}
                    </Button>
                  )}
                  {enableCompanyProfileLayoutEdit && (
                    <>
                      {!layoutEdit ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Save current state as backup
                              setBackupPanelVisibility(panelVisibility);
                              setBackupGridLayouts(gridLayouts);
                              setBackupHeaderColor(headerColor);
                              setLayoutEdit(true);
                            }}
                            className="text-xs"
                          >
                            Edit layout
                          </Button>
                          {enableCompanyProfilePdfExport ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs"
                                  disabled={!isExportReady}
                                  title={!isExportReady ? "Waiting for map and data to load" : undefined}
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  Export
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onClick={handleOpenReport} disabled={!isExportReady}>
                                  Export report (PDF)
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleExportCsv} disabled={!isExportReady}>
                                  Export CSV
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExportExcel} disabled={!isExportReady}>
                                  Export Excel
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleCopyExport} disabled={!isExportReady}>
                                  Copy to clipboard
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              Colors
                            </span>
                            <div className="flex items-center gap-2">
                              {headerColorOptions.map((color) => (
                                <button
                                  key={color}
                                  type="button"
                                  onClick={() => handleHeaderColorChange(color)}
                                  aria-label={`Set header color ${color}`}
                                  className={`h-6 w-6 rounded-full ${
                                    headerColor === color ? "ring-2 ring-foreground" : ""
                                  }`}
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="text-xs">
                                Panels
                                <SlidersHorizontal className="h-3 w-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                              <DropdownMenuLabel>Visible panels</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {PROFILE_PANELS.map((panel) => (
                                <DropdownMenuCheckboxItem
                                  key={panel.key}
                                  checked={panelVisibility[panel.key] !== false}
                                  onCheckedChange={(checked) =>
                                    setPanelVisibility((prev) => ({
                                      ...prev,
                                      [panel.key]: checked === true,
                                    }))
                                  }
                                  onSelect={(e) => e.preventDefault()}
                                >
                                  {panel.title}
                                </DropdownMenuCheckboxItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={handleShowAllPanels}>Show all</DropdownMenuItem>
                              <DropdownMenuItem onClick={handleHideAllPanels}>Hide all</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResetLayout}
                            className="text-xs"
                          >
                            Reset layout
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSaveLayout}
                            className="text-xs"
                          >
                            Save
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleExitLayout}
                            className="text-xs"
                          >
                            Exit
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  
                  {/* Fullscreen toggle - always visible, positioned last (far right) */}
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted/40 transition-colors"
                    aria-label={isProfileMaximized ? "Exit fullscreen" : "Fullscreen"}
                    onClick={() => setIsProfileMaximized((prev) => !prev)}
                  >
                    {isProfileMaximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              {/* Content panels - adjust layout here */}
              <div
                className="flex-1 overflow-hidden"
                ref={layoutHeightRef}
                style={{
                  padding: `${layoutEdgePaddingTop}px ${layoutEdgePaddingX}px ${layoutEdgePaddingBottom}px`,
                }}
              >
                <div
                  className={`relative h-full layout-grid-edit overflow-hidden ${
                    layoutEdit ? "bg-muted/40" : ""
                  }`}
                  ref={layoutWidthRef as React.Ref<HTMLDivElement>}
                  style={{
                    height: layoutCanvasHeight ? `${layoutCanvasHeight}px` : `calc(100% - ${layoutBottomInset}px)`,
                  }}
                >
                  {layoutEdit && (
                    <div
                      className="absolute pointer-events-none"
                      style={{
                        top: 0,
                        left: 0,
                        width: `${layoutGridWidth}px`,
                        height: `${layoutGridHeight}px`,
                        display: "grid",
                        gridTemplateColumns: `repeat(${gridColumns}, ${layoutColWidth}px)`,
                        gridTemplateRows: `repeat(${gridRows}, ${layoutRowHeight}px)`,
                        columnGap: `${gridMargin[0]}px`,
                        rowGap: `${gridMargin[1]}px`,
                        justifyContent: "start",
                        alignContent: "start",
                        zIndex: 0,
                      }}
                    >
                      {Array.from({ length: gridColumns * gridRows }).map((_, index) => (
                        <div
                          key={`grid-cell-${index}`}
                          className="bg-muted/30 border border-border/50"
                        />
                      ))}
                    </div>
                  )}
                  <ResponsiveGridLayout
                    className="relative z-10 h-full"
                    width={layoutWidth || 1200}
                    layouts={Object.keys(gridLayouts).length ? gridLayouts : defaultLayouts}
                    cols={{ lg: gridColumns, md: gridColumns, sm: gridColumns, xs: gridColumns, xxs: gridColumns }}
                    rowHeight={layoutRowHeight}
                    margin={gridMargin}
                    containerPadding={gridPadding}
                    dragConfig={{
                      handle: ".panel-drag-handle",
                      enabled: layoutEdit,
                      bounded: true,
                      threshold: 5,
                      cancel: ".react-resizable-handle",
                    }}
                    resizeConfig={{ enabled: layoutEdit, handles: ["se", "e", "s", "n", "w", "ne", "nw", "sw"] }}
                    constraints={[containerBounds, gridBounds, snapToGrid(1), minSize(2, 2), maxSize(gridColumns, gridRows)]}
                    compactor={noCompactor}
                    autoSize={false}
                    maxRows={layoutMaxRows}
                    onLayoutChange={(_, layouts) => setGridLayouts(clampLayouts(layouts))}
                  >
                    {visiblePanels.map((panel) => (
                      <div key={panel.key} className="h-full overflow-hidden">
                        {renderPanel(panel)}
                      </div>
                    ))}
                  </ResponsiveGridLayout>
                </div>
              </div>
              
              {/* Internal Footer */}
              <div className="px-5 py-3 border-t border-border/60 bg-card/70 flex items-center justify-between text-[10px] text-foreground shrink-0">
                <span className="text-muted-foreground">
                  © {new Date().getFullYear()} Crown copyright. Core data from Companies House, enhanced by Datadini.
                </span>
                <div className="flex items-center gap-4 text-muted-foreground" data-export-hide="true">
                  <Link
                    href={isMounted && advancedMode ? "/search?mode=advanced" : "/search"}
                    className="hover:text-foreground transition-colors"
                  >
                    Search
                  </Link>
                  <Link href="/dashboards" className="hover:text-foreground transition-colors">Dashboards</Link>
                  <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
                </div>
              </div>
              
            </div>
          </motion.div>
        </motion.div>

      {showDebug && (
        <div className="fixed bottom-4 left-4 z-50 pointer-events-auto">
          <details className="group relative">
            <summary className="cursor-pointer list-none rounded-md border border-dashed border-border/70 bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground shadow-sm">
              Company profile debug
            </summary>
            <div className="absolute bottom-full left-0 mb-2 w-[600px] max-h-[600px] overflow-auto rounded-md border border-border/70 bg-background/95 px-3 py-2 text-xs text-muted-foreground shadow-lg">
              <div>
                <span className="font-medium text-foreground">Snapshot status:</span>{" "}
                {snapshotLoading ? "loading" : snapshot ? "loaded" : snapshotError ?? "empty"}
              </div>
              <div>
                <span className="font-medium text-foreground">Snapshot source:</span> {snapshotSource}
              </div>
              <div>
                <span className="font-medium text-foreground">Snapshot fields:</span> {snapshotEntries.length}
              </div>
              <div>
                <span className="font-medium text-foreground">Raw SIC_Code:</span>{" "}
                {String(
                  getRawSnapshotValue(
                    snapshotRaw,
                    "SIC_Code",
                    "SIC_CODE",
                    "SIC_Code_1",
                    "SIC_CODE_1",
                    "SIC",
                    "sic_code",
                    "sic_code_1"
                  ) ?? "—"
                )}
              </div>
              <div>
                <span className="font-medium text-foreground">Resolved SIC:</span> {resolvedSicCode1 || "—"}
              </div>
              <div>
                <span className="font-medium text-foreground">Snapshot payload:</span>
                <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-background px-2 py-1">
{JSON.stringify(snapshotRaw ?? null, null, 2)}
                </pre>
              </div>
              <div>
                <span className="font-medium text-foreground">Map coords:</span>{" "}
                {mapCoords ? `${mapCoords.lat}, ${mapCoords.lon}` : "none"}
              </div>
              <div>
                <span className="font-medium text-foreground">Officers:</span>{" "}
                {officersLoading ? "loading" : officers?.summary?.total_officers ?? "n/a"}
              </div>
              <div>
                <span className="font-medium text-foreground">PSC:</span>{" "}
                {pscLoading ? "loading" : psc?.summary?.total_pscs ?? "n/a"}
              </div>
              <div>
                <span className="font-medium text-foreground">Gazette:</span>{" "}
                {gazetteLoading ? "loading" : gazette?.summary?.total_notices ?? "n/a"}
              </div>
              <div>
                <span className="font-medium text-foreground">Contacts:</span>{" "}
                {contactsLoading ? "loading" : contacts ? `${contacts.length} record(s)` : "empty"}
              </div>
              <div>
                <span className="font-medium text-foreground">Contacts payload:</span>
                <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-background px-2 py-1">
{JSON.stringify(contacts ?? null, null, 2)}
                </pre>
              </div>
              <div>
                <span className="font-medium text-foreground">Gazette payload:</span>
                <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-background px-2 py-1">
{JSON.stringify(gazette ?? null, null, 2)}
                </pre>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* Fullscreen overlay */}
      {isMounted && isProfileMaximized && !reportMode && createPortal(
        <div className="fixed inset-0 z-50 bg-background">
          <div className="h-full w-full overflow-auto">
            <div className="w-full h-full bg-card flex flex-col">
              {/* Header */}
              <div className="px-5 py-3 flex items-start justify-between gap-4 shrink-0 border-b" suppressHydrationWarning>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex items-center gap-x-3 min-w-0 overflow-hidden text-[12px] sm:text-[13px] text-muted-foreground">
                      <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
                        {snapshot?.Company_Name || company.company_name}
                      </h1>
                      {/* Fixed space for company logo/favicon */}
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shrink-0" suppressHydrationWarning>
                        {contacts && contacts.length > 0 && (contacts[0].Website || contacts[0].Domain) && (
                          <img
                            src={`https://${contacts[0].Domain || new URL(contacts[0].Website!).hostname}/apple-touch-icon.png`}
                            alt="Company logo"
                            className="w-5 h-5 sm:w-6 sm:h-6 object-contain hidden"
                            onLoad={(e) => {
                              e.currentTarget.classList.remove('hidden');
                            }}
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.classList.add('hidden');
                              if (target.src.includes('apple-touch-icon.png')) {
                                target.src = target.src.replace('apple-touch-icon.png', 'favicon-32x32.png');
                              } else if (target.src.includes('favicon-32x32.png')) {
                                target.src = target.src.replace('favicon-32x32.png', 'favicon.ico');
                              }
                            }}
                          />
                        )}
                      </div>
                      <span className="font-mono whitespace-nowrap">
                        {company.company_number}
                      </span>
                      {resolvedType && (
                        <span className="whitespace-nowrap">· {resolvedType}</span>
                      )}
                      {resolvedIncorporation && (
                        <span className="whitespace-nowrap">
                          · Est. {formatDate(resolvedIncorporation)}
                        </span>
                      )}
                      {isNumber(resolvedAgeYears) && (
                        <span className="whitespace-nowrap">
                          · Age {resolvedAgeYears.toFixed(1)} yrs
                        </span>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      className="h-7 text-[10px] shrink-0"
                    >
                      <a href={resolvedCompaniesHouseLink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3 w-3 mr-1" />Companies House
                      </a>
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Fullscreen toggle */}
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted/40 transition-colors"
                    aria-label="Exit fullscreen"
                    onClick={() => setIsProfileMaximized(false)}
                  >
                    <Minimize2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto px-5 py-4">
                {visiblePanels.map((panel) => (
                  <div key={panel.key} className="mb-4">
                    {renderPanel(panel)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

    </>
  );
}
